import { Suspense, lazy, useEffect } from "react";
import { rememberRoute } from "@/lib/ctaSource";
import CommercialLinkNormalizer from "@/components/CommercialLinkNormalizer";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Logo } from "@/components/ui/Logo";
import { CookieConsentManager } from "@/components/CookieConsentManager";
import { DynamicTrackingScripts } from "@/components/DynamicTrackingScripts";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { IT_ORDER_ALIAS_PATH, IT_ORDER_ALIAS_TARGET } from "@/lib/it-order-alias.js";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContextNew";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { usePageTracking } from "@/hooks/usePageTracking";
import { OrderProvider } from "@/context/OrderContext";
import { ProtectedRoute, ClientRoute, AgentRoute, AdminRoute, InternalRoute, SecureAdminRoute } from "@/components/portal/ProtectedRoute";
import { RoleProtectedRoute } from "@/components/auth/RoleProtectedRoute";
import { UserRole } from "@/types/roles";
import { AgentPermissionRoute } from "@/components/agent/AgentPermissionRoute";
import { ALL_COUNTRY_ROUTE_SLUGS } from "@/data/country-routes";
import { ALL_CITY_ROUTE_SLUGS, ALL_AGENCY_ROUTE_SLUGS, SOFT_DEV_CITY_KEYS, SOFT_DEV_REGION_KEYS } from "@/data/route-slugs.generated";
import CaseStudiesPage from "./pages/CaseStudiesPage";

// Lazy-load UI components not needed for initial paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const SingleSessionListener = lazy(() => import("@/components/auth/SingleSessionListener").then(m => ({ default: m.SingleSessionListener })));

// Lazy load pages for performance
const HomePage = lazy(() => import("./pages/HomePage"));
const PriorityServicePage = lazy(() => import("./pages/PriorityServicePage"));
const ContactCenterScorecardPage = lazy(() => import("./pages/ContactCenterScorecardPage"));
const LargeProgramsPage = lazy(() => import("./pages/LargeProgramsPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const BPOServicesPage = lazy(() => import("./pages/BPOServicesPage"));
const DigitalStudioPage = lazy(() => import("./pages/DigitalStudioPage"));
const IndustriesPage = lazy(() => import("./pages/IndustriesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const QuoteEstimatorPage = lazy(() => import("./pages/QuoteEstimatorPage"));
const BuyerGuidePage = lazy(() => import("./pages/BuyerGuidePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const CareersPage = lazy(() => import("./pages/CareersUnifiedPage"));
const CareersLandingPage = lazy(() => import("./pages/CareersLandingPage"));
const JobsListPage = lazy(() => import("./pages/careers/JobsListPage"));
const JobDetailNewPage = lazy(() => import("./pages/careers/JobDetailNewPage"));
const FreelancePage = lazy(() => import("./pages/careers/FreelancePage"));
const MissionDetailPage = lazy(() => import("./pages/careers/MissionDetailPage"));
const FreelancerDetailPage = lazy(() => import("./pages/FreelancerDetailPage"));
const ApplyPage = lazy(() => import("./pages/careers/ApplyPage"));
const DashboardPreviewPage = lazy(() => import("./pages/careers/DashboardPreviewPage"));
const RegionsPage = lazy(() => import("./pages/careers/RegionsPage"));
const WhyJoinPage = lazy(() => import("./pages/careers/WhyJoinPage"));
const AuthLoginPage = lazy(() => import("./pages/careers/AuthLoginPage"));
const AuthRegisterPage = lazy(() => import("./pages/careers/AuthRegisterPage"));
const AuthResetPasswordPage = lazy(() => import("./pages/careers/AuthResetPasswordPage"));
const EmployerDashboardPage = lazy(() => import("./pages/careers/EmployerDashboardPage"));
const EmployerPostJobPage = lazy(() => import("./pages/careers/EmployerPostJobPage"));
const AdminModerationPage = lazy(() => import("./pages/careers/AdminModerationPage"));
const UserDashboardPage = lazy(() => import("./pages/careers/UserDashboardPage"));
const UserProfilePage = lazy(() => import("./pages/careers/UserProfilePage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const SpontaneousApplicationPage = lazy(() => import("./pages/SpontaneousApplicationPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const JobBoardPage = lazy(() => import("./pages/JobBoardPage"));

// Editorial Guides
const HowToChooseBPOPartnerGuide = lazy(() => import("./pages/guides/HowToChooseBPOPartnerGuide"));
const CompleteOutsourcingGuide2026 = lazy(() => import("./pages/guides/CompleteOutsourcingGuide2026"));
const InhouseVsOutsourcingGuide = lazy(() => import("./pages/guides/InhouseVsOutsourcingGuide"));
const GDPRComplianceOutsourcingGuide = lazy(() => import("./pages/guides/GDPRComplianceOutsourcingGuide"));
const NearshoreVsOffshoreGuide = lazy(() => import("./pages/guides/NearshoreVsOffshoreGuide"));
const AICallCenterGuide2026 = lazy(() => import("./pages/guides/AICallCenterGuide2026"));
const OutsourcingROICalculatorGuide = lazy(() => import("./pages/guides/OutsourcingROICalculatorGuide"));

// Part 5: Strategic Pages
const WhyMoroccoPage = lazy(() => import("./pages/WhyMoroccoPage"));
const AIContactCenterPage = lazy(() => import("./pages/AIContactCenter"));
const SitemapIndexPage = lazy(() => import("./pages/SitemapIndexPage"));
const CountryLandingPage = lazy(() => import("./pages/landing/CountryLandingPage"));
const IndustryLandingPage = lazy(() => import("./pages/landing/IndustryLandingPage"));
const ComparisonLandingPage = lazy(() => import("./pages/landing/ComparisonLandingPage"));
const ContinentLandingPage = lazy(() => import("./pages/landing/ContinentLandingPage"));
const RegionLandingPage = lazy(() => import("./pages/landing/RegionLandingPage"));
const CallCenterCityPage = lazy(() => import("./pages/CallCenterCityPage"));
const CitiesHubPage = lazy(() => import("./pages/CitiesHubPage"));
const CityBPOLandingPage = lazy(() => import("./pages/landing/CityBPOLandingPage"));
const SoftwareDevCityPage = lazy(() => import("./pages/landing/SoftwareDevCityPage"));
const SoftwareDevRegionPage = lazy(() => import("./pages/landing/SoftwareDevRegionPage"));
const FrenchServicePage = lazy(() => import("./pages/FrenchServicePage"));
const SpanishServicePage = lazy(() => import("./pages/SpanishServicePage"));
const ArabicServicePage = lazy(() => import("./pages/ArabicServicePage"));
const GermanServicePage = lazy(() => import("./pages/GermanServicePage"));
const PortugueseServicePage = lazy(() => import("./pages/PortugueseServicePage"));
const ItalianServicePage = lazy(() => import("./pages/ItalianServicePage"));
const DutchServicePage = lazy(() => import("./pages/DutchServicePage"));
const PolishServicePage = lazy(() => import("./pages/PolishServicePage"));
const HindiServicePage = lazy(() => import("./pages/HindiServicePage"));
const TurkishServicePage = lazy(() => import("./pages/TurkishServicePage"));
const JapaneseServicePage = lazy(() => import("./pages/JapaneseServicePage"));
const KoreanServicePage = lazy(() => import("./pages/KoreanServicePage"));
const ChineseServicePage = lazy(() => import("./pages/ChineseServicePage"));
const IndonesianServicePage = lazy(() => import("./pages/IndonesianServicePage"));
const MalayServicePage = lazy(() => import("./pages/MalayServicePage"));
const ThaiServicePage = lazy(() => import("./pages/ThaiServicePage"));
const VietnameseServicePage = lazy(() => import("./pages/VietnameseServicePage"));
const SwahiliServicePage = lazy(() => import("./pages/SwahiliServicePage"));
const BengaliServicePage = lazy(() => import("./pages/BengaliServicePage"));
const TagalogServicePage = lazy(() => import("./pages/TagalogServicePage"));
const SwedishServicePage = lazy(() => import("./pages/SwedishServicePage"));
const RomanianServicePage = lazy(() => import("./pages/RomanianServicePage"));
const CzechServicePage = lazy(() => import("./pages/CzechServicePage"));
const DanishServicePage = lazy(() => import("./pages/DanishServicePage"));
const FinnishServicePage = lazy(() => import("./pages/FinnishServicePage"));
const GreekServicePage = lazy(() => import("./pages/GreekServicePage"));
const HungarianServicePage = lazy(() => import("./pages/HungarianServicePage"));
const IndustryCountryLandingPage = lazy(() => import("./pages/landing/IndustryCountryLandingPage"));
const CaseStudiesPageNew = lazy(() => import("./pages/CaseStudiesPageNew"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const IndustriesPageHub = lazy(() => import("./pages/IndustriesPageHub"));
const PrivacyPageNew = lazy(() => import("./pages/legal/PrivacyPageNew"));
const TermsPageNew = lazy(() => import("./pages/legal/TermsPageNew"));
const CookiesPageNew = lazy(() => import("./pages/legal/CookiesPageNew"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ServiceSlugCatchAll = lazy(() => import("./pages/ServiceSlugCatchAll"));
const LeadershipPage = lazy(() => import("./pages/LeadershipPage"));
const WhyUsPage = lazy(() => import("./pages/WhyUsPage"));
const LegalNoticePage = lazy(() => import("./pages/legal/LegalNoticePage"));
// Part 8: New Strategic Pages
const ROICalculatorPage = lazy(() => import("./pages/ROICalculatorPage"));
const CostCalculatorPage = lazy(() => import("./pages/CostCalculatorPage"));
const PricingCalculatorPage = lazy(() => import("./pages/PricingCalculatorPage"));
const ExtendedComparisonPage = lazy(() => import("./pages/comparisons/ExtendedComparisonPage"));
const ComparisonPageV2 = lazy(() => import("./pages/comparisons/ComparisonPageV2"));
const FAQHubPage = lazy(() => import("./pages/FAQHubPage"));
const SecurityCompliancePage = lazy(() => import("./pages/SecurityCompliancePage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const SLAGuaranteesPage = lazy(() => import("./pages/SLAGuaranteesPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const MoroccoVsIndiaPage = lazy(() => import("./pages/comparisons/MoroccoVsIndiaPage"));
const NearshoreVsOffshorePage = lazy(() => import("./pages/comparisons/NearshoreVsOffshorePage"));
const FreelancersDirectoryPage = lazy(() => import("./pages/FreelancersDirectoryPage"));
const OutsourcingCountryPage = lazy(() => import("./pages/landing/OutsourcingCountryPage"));
const OutsourcingCountryHubPage = lazy(() => import("./pages/OutsourcingCountryHubPage"));
const FreelanceCategoryPage = lazy(() => import("./pages/landing/FreelanceCategoryPage"));
const CareerRolePage = lazy(() => import("./pages/landing/CareerRolePage"));
const IndustrySEOPage = lazy(() => import("./pages/industries/IndustrySEOPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const CaseStudyDetailPage = lazy(() => import("./pages/CaseStudyDetailPage"));
const ServiceComparisonPage = lazy(() => import("./pages/ServiceComparisonPage"));
const WhyChooseUsPage = lazy(() => import("./pages/WhyChooseUsPage"));
const GlobalFAQPage = lazy(() => import("./pages/GlobalFAQPage"));
const HTMLSitemapPage = lazy(() => import("./pages/HTMLSitemapPage"));
const ResourceGuidePage = lazy(() => import("./components/ResourceGuidePage"));

// Shop Pages (New Service Ordering System)
const ServicesCataloguePage = lazy(() => import("./pages/shop/ServicesCataloguePage"));
const ServiceConfiguratorPage = lazy(() => import("./pages/shop/ServiceConfiguratorPage"));
const ServiceDetailPage = lazy(() => import("./pages/shop/ServiceDetailPage"));
const CheckoutPage = lazy(() => import("./pages/shop/CheckoutPage"));
const ShopPricingPage = lazy(() => import("./pages/shop/ShopPricingPage"));

// BPO Service Detail Pages
const InboundCustomerCarePage = lazy(() => import("./pages/services/bpo/InboundCustomerCarePage"));
const TechnicalSupportPage = lazy(() => import("./pages/services/bpo/TechnicalSupportPage"));
const OutboundSalesPage = lazy(() => import("./pages/services/bpo/OutboundSalesPage"));
const RetentionWinbackPage = lazy(() => import("./pages/services/bpo/RetentionWinbackPage"));
const SDRAppointmentSettingPage = lazy(() => import("./pages/services/bpo/SDRAppointmentSettingPage"));
const MarketResearchPage = lazy(() => import("./pages/services/bpo/MarketResearchPage"));
const DigitalEngagementPage = lazy(() => import("./pages/services/bpo/DigitalEngagementPage"));
const TrustSafetyPage = lazy(() => import("./pages/services/bpo/TrustSafetyPage"));
const AIOpsBackofficePage = lazy(() => import("./pages/services/bpo/AIOpsBackofficePage"));
const MultilingualSupportPage = lazy(() => import("./pages/services/bpo/MultilingualSupportPage"));
// New BPO Service Pages
const OrderManagementPage = lazy(() => import("./pages/services/bpo/OrderManagementPage"));
const BillingCollectionsPage = lazy(() => import("./pages/services/bpo/BillingCollectionsPage"));
const CustomerRetentionPage = lazy(() => import("./pages/services/bpo/CustomerRetentionPage"));
const LeadQualificationPage = lazy(() => import("./pages/services/bpo/LeadQualificationPage"));
const LiveChatMessagingPage = lazy(() => import("./pages/services/bpo/LiveChatMessagingPage"));
const SocialMediaCarePage = lazy(() => import("./pages/services/bpo/SocialMediaCarePage"));
const CustomerSuccessSaaSPage = lazy(() => import("./pages/services/bpo/CustomerSuccessSaaSPage"));
const CollectionsPaymentPage = lazy(() => import("./pages/services/bpo/CollectionsPaymentPage"));
const QACoachingPage = lazy(() => import("./pages/services/bpo/QACoachingPage"));
// Expansion BPO Pages
const FinanceAccountingPage = lazy(() => import("./pages/services/bpo/FinanceAccountingPage"));
const HRPayrollPage = lazy(() => import("./pages/services/bpo/HRPayrollPage"));
const HealthcareBPOPage = lazy(() => import("./pages/services/bpo/HealthcareBPOPage"));
const LogisticsSupplyChainPage = lazy(() => import("./pages/services/bpo/LogisticsSupplyChainPage"));
const VirtualAssistantPage = lazy(() => import("./pages/services/bpo/VirtualAssistantPage"));
const RecruitmentStaffingPage = lazy(() => import("./pages/services/bpo/RecruitmentStaffingPage"));
// New BPO Pages (missing from navigation)
const FraudDetectionPage = lazy(() => import("./pages/services/bpo/FraudDetectionPage"));
const ComplianceMonitoringPage = lazy(() => import("./pages/services/bpo/ComplianceMonitoringPage"));
const ContentReviewPage = lazy(() => import("./pages/services/bpo/ContentReviewPage"));
const DataEntryProcessingPage = lazy(() => import("./pages/services/bpo/DataEntryProcessingPage"));
// New Digital Factory Pages
const DataEngineeringBIPage = lazy(() => import("./pages/services/digital/DataEngineeringBIPage"));
const CybersecurityAppSecPage = lazy(() => import("./pages/services/digital/CybersecurityAppSecPage"));
const ManagedServicesPage = lazy(() => import("./pages/services/digital/ManagedServicesPage"));

// Digital Studio Service Detail Pages
const CustomSaaSPage = lazy(() => import("./pages/services/digital/CustomSaaSPage"));
const EnterprisePortalsPage = lazy(() => import("./pages/services/digital/EnterprisePortalsPage"));
const EcommercePage = lazy(() => import("./pages/services/digital/EcommercePage"));
const APIIntegrationPage = lazy(() => import("./pages/services/digital/APIIntegrationPage"));
const MobileAppsPage = lazy(() => import("./pages/services/digital/MobileAppsPage"));
const UIUXDesignPage = lazy(() => import("./pages/services/digital/UIUXDesignPage"));
const CloudDevOpsPage = lazy(() => import("./pages/services/digital/CloudDevOpsPage"));
const GrowthMarketingPage = lazy(() => import("./pages/services/digital/GrowthMarketingPage"));
const EmergingTechAIPage = lazy(() => import("./pages/services/digital/EmergingTechAIPage"));
const TechStackShowcasePage = lazy(() => import("./pages/services/digital/TechStackShowcasePage"));
const SynergyPage = lazy(() => import("./pages/services/digital/SynergyPage"));
const QualityEngineeringPage = lazy(() => import("./pages/services/digital/QualityEngineeringPage"));
// Expansion Digital Pages
const AIMLDevelopmentPage = lazy(() => import("./pages/services/digital/AIMLDevelopmentPage"));
const BlockchainWeb3Page = lazy(() => import("./pages/services/digital/BlockchainWeb3Page"));
const IoTDevelopmentPage = lazy(() => import("./pages/services/digital/IoTDevelopmentPage"));
const LegacyModernizationPage = lazy(() => import("./pages/services/digital/LegacyModernizationPage"));
const StaffAugmentationPage = lazy(() => import("./pages/services/digital/StaffAugmentationPage"));
const ARVRDevelopmentPage = lazy(() => import("./pages/services/digital/ARVRDevelopmentPage"));
const DigitalTransformationDigitalPage = lazy(() => import("./pages/services/digital/DigitalTransformationPage"));
const DataScienceAnalyticsPage = lazy(() => import("./pages/services/digital/DataScienceAnalyticsPage"));
const QATestingPage = lazy(() => import("./pages/services/digital/QATestingPage"));
const DevOpsCloudMigrationPage = lazy(() => import("./pages/services/digital/DevOpsCloudMigrationPage"));
// New Digital Studio Pages (missing from navigation)
const PenetrationTestingPage = lazy(() => import("./pages/services/digital/PenetrationTestingPage"));
const SOCAsAServicePage = lazy(() => import("./pages/services/digital/SOCAsAServicePage"));
const MobileDevelopmentPage = lazy(() => import("./pages/services/digital/MobileDevelopmentPage"));
const WebDevelopmentDigitalPage = lazy(() => import("./pages/services/digital/WebDevelopmentDigitalPage"));

// SEO Landing Pages
const CallCenterLandingPage = lazy(() => import("./pages/landing/CallCenterLandingPage"));
const WebDevelopmentLandingPage = lazy(() => import("./pages/landing/WebDevelopmentLandingPage"));
const AppDevelopmentLandingPage = lazy(() => import("./pages/landing/AppDevelopmentLandingPage"));

const CallCenterServicePage = lazy(() => import("./pages/demos/CallCenterServicePage"));
// Required Service Pages (exact routes from requirements)
const CustomerSupportPage = lazy(() => import("./pages/services/CustomerSupportPage"));
const TelesalesPage = lazy(() => import("./pages/services/TelesalesPage"));
const BackofficePage = lazy(() => import("./pages/services/BackofficePage"));
const ContentModerationPage = lazy(() => import("./pages/services/ContentModerationPage"));
const WebDevelopmentPage = lazy(() => import("./pages/services/WebDevelopmentPage"));
const MobileAppsServicePage = lazy(() => import("./pages/services/MobileAppsServicePage"));
const AISolutionsPage = lazy(() => import("./pages/services/AISolutionsPage"));

// Part 4: New BPO Service Pages (Template-based)
const InboundCustomerSupportPage = lazy(() => import("./pages/services/bpo/InboundCustomerSupportPage"));
const OutboundSalesPageNew = lazy(() => import("./pages/services/bpo/OutboundSalesPage"));
// TechnicalSupportPageNew removed - replaced by TechnicalSupportPage (line 62)
const BackOfficeDataProcessingPage = lazy(() => import("./pages/services/bpo/BackOfficeDataProcessingPage"));
const ChatEmailSupportPage = lazy(() => import("./pages/services/bpo/ChatEmailSupportPage"));
const SocialMediaManagementPage = lazy(() => import("./pages/services/bpo/SocialMediaManagementPage"));
const ContentModerationPageNew = lazy(() => import("./pages/services/bpo/ContentModerationPage"));
const WhatsAppBusinessSupportPage = lazy(() => import("./pages/services/bpo/WhatsAppBusinessSupportPage"));
const PhoneSupportPage = lazy(() => import("./pages/services/bpo/PhoneSupportPage"));
const TwentyFourSevenSupportPage = lazy(() => import("./pages/services/bpo/TwentyFourSevenSupportPage"));
const EmailSupportPage = lazy(() => import("./pages/services/bpo/EmailSupportPage"));
const CityAgencyLandingPage = lazy(() => import("./pages/landing/CityAgencyLandingPage"));
const CountryAgencyLandingPage = lazy(() => import("./pages/landing/CountryAgencyLandingPage"));
const CityServiceLandingPage = lazy(() => import("./pages/landing/CityServiceLandingPage"));
const ContinentHubPage = lazy(() => import("./pages/landing/ContinentHubPage"));
const ServiceDirectoryPage = lazy(() => import("./pages/ServiceDirectoryPage"));
const WebinarsPage = lazy(() => import("./pages/WebinarsPage"));
const AcademyPage = lazy(() => import("./pages/AcademyPage"));
const ContactOfficePage = lazy(() => import("./pages/contact/ContactOfficePage"));
const LongTailLandingPage = lazy(() => import("./pages/LongTailLandingPage"));
const SEOAuditPage = lazy(() => import("./pages/SEOAuditPage"));
const BPOServiceLandingPage = lazy(() => import("./pages/BPOServiceLandingPage"));
const DigitalStudioLandingPage = lazy(() => import("./pages/DigitalStudioLandingPage"));
const IndustryVerticalLandingPage = lazy(() => import("./pages/IndustryVerticalLandingPage"));
const TransversalServiceLandingPage = lazy(() => import("./pages/TransversalServiceLandingPage"));
const ServiceCountryLandingPage = lazy(() => import("./pages/ServiceCountryLandingPage"));

// Part 4: New Digital Service Pages (Template-based)
const APIIntegrationPageNew = lazy(() => import("./pages/services/digital/APIIntegrationPage"));
const UIUXDesignPageNew = lazy(() => import("./pages/services/digital/UIUXDesignPage"));
const CloudDevOpsPageNew = lazy(() => import("./pages/services/digital/CloudDevOpsPage"));
// AIAutomationPage removed — `services/digital/ai-automation` already redirects to `services/digital-studio/emerging-tech-ai`

// Part 4: Consulting Service Pages (Template-based)
const CXStrategyAuditPage = lazy(() => import("./pages/services/consulting/CXStrategyAuditPage"));
const DigitalTransformationPage = lazy(() => import("./pages/services/consulting/DigitalTransformationPage"));

// Portal Pages
const PortalLoginPage = lazy(() => import("./pages/portal/LoginPage"));
const RegisterPage = lazy(() => import("./pages/portal/RegisterPage"));
const UnauthorizedPage = lazy(() => import("./pages/portal/UnauthorizedPage"));
const AccountDisabledPage = lazy(() => import("./pages/portal/AccountDisabledPage"));
const PortalRedirectPage = lazy(() => import("./pages/portal/PortalRedirectPage"));

// Canonical Auth Pages (EN source of truth)
const AuthPage = lazy(() => import("./pages/auth/AuthPage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./pages/auth/SignupPageNew"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const LogoutPage = lazy(() => import("./pages/auth/LogoutPage"));
const InvitePage = lazy(() => import("./pages/auth/InvitePage"));
const MFAPage = lazy(() => import("./pages/auth/MFAPage"));
const AuthCallbackPage = lazy(() => import("./pages/auth/AuthCallbackPage"));
const OAuthConsent = lazy(() => import("./pages/auth/OAuthConsent"));
const AuthErrorPage = lazy(() => import("./pages/auth/AuthErrorPage"));
const AdminSecuritySettingsPage = lazy(() => import("./pages/admin/settings/SecuritySettingsPage"));
// Platform Setup Wizard (First-run configuration)
const PlatformSetupWizard = lazy(() => import("./pages/admin/PlatformSetupWizard"));
const AdminOnboardingPage = lazy(() => import("./pages/admin/AdminOnboardingPage"));

// QA Results & Audit Pages
const QAResultsPage = lazy(() => import("./pages/admin/QAResultsPage"));
const LinkAuditPage = lazy(() => import("./pages/admin/LinkAuditPage"));
const ErrorLogPage = lazy(() => import("./pages/admin/ErrorLogPage"));
const GapReportPage = lazy(() => import("./pages/admin/GapReportPage"));

// Phase 1: Project Map & Health Pages
const AdminSitemapPage = lazy(() => import("./pages/admin/AdminSitemapPage"));
const AdminHealthPage = lazy(() => import("./pages/admin/AdminHealthPage"));
const AdminRoutingSimulationPage = lazy(() => import("./pages/admin/AdminRoutingSimulationPage"));
const GlobalSettingsPage = lazy(() => import("./pages/admin/GlobalSettingsPage"));
const SystemHealthPage = lazy(() => import("./pages/admin/SystemHealthPage"));

// Phase 2: Routing Module Pages
const AdminRoutingSkillsPage = lazy(() => import("./pages/admin/routing/AdminRoutingSkillsPage"));
const AdminRoutingSLAsPage = lazy(() => import("./pages/admin/routing/AdminRoutingSLAsPage"));
const AdminRoutingPoliciesPage = lazy(() => import("./pages/admin/routing/AdminRoutingPoliciesPage"));
const AdminRoutingDispositionsPage = lazy(() => import("./pages/admin/routing/AdminRoutingDispositionsPage"));
const AdminRoutingSchedulesPage = lazy(() => import("./pages/admin/routing/AdminRoutingSchedulesPage"));

// Phase 2: WFM Module Pages
const AdminWFMForecastPage = lazy(() => import("./pages/admin/wfm/AdminWFMForecastPage"));
const AdminWFMAdherencePage = lazy(() => import("./pages/admin/wfm/AdminWFMAdherencePage"));
const AdminWFMIntradayPage = lazy(() => import("./pages/admin/wfm/AdminWFMIntradayPage"));
const AdminTimeSettingsPage = lazy(() => import("./pages/admin/wfm/AdminTimeSettingsPage"));

// Phase 2: Analytics Module Pages
const AdminAnalyticsRealtimePage = lazy(() => import("./pages/admin/analytics/AdminAnalyticsRealtimePage"));
const AdminAnalyticsExportsPage = lazy(() => import("./pages/admin/analytics/AdminAnalyticsExportsPage"));

// Phase 2: Quality Module Pages
const AdminQualityFormsPage = lazy(() => import("./pages/admin/quality/AdminQualityFormsPage"));
const AdminQualityEvaluationsPage = lazy(() => import("./pages/admin/quality/AdminQualityEvaluationsPage"));
const AdminQualityCoachingPage = lazy(() => import("./pages/admin/quality/AdminQualityCoachingPage"));

// Phase 2: Digital Module Pages
const AdminDigitalChannelsPage = lazy(() => import("./pages/admin/digital/AdminDigitalChannelsPage"));
const AdminDigitalTemplatesPage = lazy(() => import("./pages/admin/digital/AdminDigitalTemplatesPage"));
const AdminDigitalRoutingPage = lazy(() => import("./pages/admin/digital/AdminDigitalRoutingPage"));

// Phase 2: Security Module Pages
const AdminSecuritySSOPage = lazy(() => import("./pages/admin/security/AdminSecuritySSOPage"));
const AdminSecurityMFAPage = lazy(() => import("./pages/admin/security/AdminSecurityMFAPage"));
const AdminSecurityRetentionPage = lazy(() => import("./pages/admin/security/AdminSecurityRetentionPage"));
const AdminSecurityPIIMaskingPage = lazy(() => import("./pages/admin/security/AdminSecurityPIIMaskingPage"));

// Phase 2: E2E Test Matrix Page
const AdminTestsPage = lazy(() => import("./pages/admin/AdminTestsPage"));

// Phase 2: Integrations Module Pages
const AdminIntegrationsWebhooksPage = lazy(() => import("./pages/admin/integrations/AdminIntegrationsWebhooksPage"));
const AdminIntegrationsAPIKeysPage = lazy(() => import("./pages/admin/integrations/AdminIntegrationsAPIKeysPage"));
const AdminIntegrationsCTIPage = lazy(() => import("./pages/admin/integrations/AdminIntegrationsCTIPage"));
const AdminIntegrationsCRMPage = lazy(() => import("./pages/admin/integrations/AdminIntegrationsCRMPage"));

// Phase 2: Voice Module Pages
const AdminVoiceNumbersPage = lazy(() => import("./pages/admin/voice/AdminVoiceNumbersPage"));
const AdminVoiceTrunksPage = lazy(() => import("./pages/admin/voice/AdminVoiceTrunksPage"));
const AdminVoiceIVRPage = lazy(() => import("./pages/admin/voice/AdminVoiceIVRPage"));

// Phase 2: Settings Module Pages
const AdminSettingsFeatureFlagsPage = lazy(() => import("./pages/admin/settings/AdminSettingsFeatureFlagsPage"));
const AdminSettingsEnvironmentsPage = lazy(() => import("./pages/admin/settings/AdminSettingsEnvironmentsPage"));

// New Admin Pages
const AdminTenantsPage = lazy(() => import("./pages/admin/AdminTenantsPage"));
const AdminRBACPage = lazy(() => import("./pages/admin/AdminRBACPage"));
const AdminSecurityPage = lazy(() => import("./pages/admin/AdminSecurityPage"));
const AdminProgramsPage = lazy(() => import("./pages/admin/AdminProgramsPage"));
const AdminOrdersPage = lazy(() => import("./pages/admin/AdminOrdersPage"));
const AdminLeadsPage = lazy(() => import("./pages/admin/AdminLeadsPage"));
const AdminRecruitmentPage = lazy(() => import("./pages/admin/AdminRecruitmentPage"));
const AdminFreelancePage = lazy(() => import("./pages/admin/AdminFreelancePage"));
const AdminApprovalsPage = lazy(() => import("./pages/admin/AdminApprovalsPage"));
const AdminWFMPage = lazy(() => import("./pages/admin/AdminWFMPage"));
const AdminQualityPage = lazy(() => import("./pages/admin/AdminQualityPage"));
const AdminBillingFullPage = lazy(() => import("./pages/admin/AdminBillingFullPage"));
const AdminSettingsHub = lazy(() => import("./pages/admin/AdminSettingsHub"));
const AdminProjectsPage = lazy(() => import("./pages/admin/AdminProjectsPage"));
const AdminVoicePage = lazy(() => import("./pages/admin/AdminVoicePage"));
const AdminMailPage = lazy(() => import("./pages/admin/AdminMailPage"));
const AdminSIPPage = lazy(() => import("./pages/admin/AdminSIPPage"));
const AdminQueuePage = lazy(() => import("./pages/admin/AdminQueuePage"));
const AdminPricingPage = lazy(() => import("./pages/admin/AdminPricingPage"));
const AdminVoiceQueuesPage = lazy(() => import("./pages/admin/AdminVoiceQueuesPage"));
const AdminVoiceCampaignsPage = lazy(() => import("./pages/admin/AdminVoiceCampaignsPage"));
const AdminMailTemplatesPage = lazy(() => import("./pages/admin/AdminMailTemplatesPage"));
const AdminAgentEmailsPage = lazy(() => import("./pages/admin/AdminAgentEmailsPage"));
const AdminMetadataApiKeyPage = lazy(() => import("./pages/admin/AdminMetadataApiKeyPage"));
const AdminLeadRecettePage = lazy(() => import("./pages/admin/AdminLeadRecettePage"));
const AdminValidatedTermsPage = lazy(() => import("./pages/admin/AdminValidatedTermsPage"));
const AdminAgentEmailsManagementPage = lazy(() => import("./pages/admin/AdminAgentEmailsManagementPage"));
const AdminLiveOpsPage = lazy(() => import("./pages/admin/AdminLiveOpsPage"));
const AdminInboxPage = lazy(() => import("./pages/admin/AdminInboxPage"));
// Client Portal
const ClientDashboard = lazy(() => import("./pages/portal/client/ClientDashboard"));
const ClientTickets = lazy(() => import("./pages/portal/client/ClientTickets"));
const NewTicket = lazy(() => import("./pages/portal/client/NewTicket"));
const TicketDetail = lazy(() => import("./pages/portal/client/TicketDetail"));

// Agent Portal
const AgentDashboard = lazy(() => import("./pages/portal/agent/AgentDashboard"));
const AgentWorkspaceDashboard = lazy(() => import("./pages/agent/AgentWorkspaceDashboard"));
const AgentSchedulePage = lazy(() => import("./pages/agent/AgentSchedulePage"));
const AgentQAPage = lazy(() => import("./pages/agent/AgentQAPage"));
const AgentKBPage = lazy(() => import("./pages/agent/AgentKBPage"));
const AgentTimePage = lazy(() => import("./pages/agent/AgentTimePage"));
const AgentCoachingPage = lazy(() => import("./pages/agent/AgentCoachingPage"));
const AgentLearningPage = lazy(() => import("./pages/agent/AgentLearningPage"));
const AgentAnnouncementsPage = lazy(() => import("./pages/agent/AgentAnnouncementsPage"));
const AgentTicketsPage = lazy(() => import("./pages/agent/AgentTicketsPage"));
const AgentSettingsPage = lazy(() => import("./pages/agent/AgentSettingsPage"));
const AgentPhonePage = lazy(() => import("./pages/agent/AgentPhonePage"));
const AgentMailPage = lazy(() => import("./pages/agent/AgentMailPage"));
const AgentTicketDetailPage = lazy(() => import("./pages/agent/AgentTicketDetailPage"));
const AgentCoveragePage = lazy(() => import("./pages/portal/agent/AgentCoveragePage"));
const AgentProductionReadinessPage = lazy(() => import("./pages/portal/agent/AgentProductionReadinessPage"));

// Enterprise Portal Pages
const ProgramSetupWizard = lazy(() => import("./pages/portal/enterprise/ProgramSetupWizard"));
const KnowledgeOpsPage = lazy(() => import("./pages/portal/enterprise/KnowledgeOpsPage"));
const SLADesignerPage = lazy(() => import("./pages/portal/enterprise/SLADesignerPage"));
const LiveWallboardPage = lazy(() => import("./pages/portal/enterprise/LiveWallboardPage"));
const CommandCenterPage = lazy(() => import("./pages/portal/enterprise/CommandCenterPage"));
const DataExportPage = lazy(() => import("./pages/portal/enterprise/DataExportPage"));
const ServiceCatalogPage = lazy(() => import("./pages/portal/enterprise/ServiceCatalogPage"));
const OrgManagementPage = lazy(() => import("./pages/portal/enterprise/OrgManagementPage"));
const BillingSettingsPage = lazy(() => import("./pages/portal/enterprise/BillingSettingsPage"));
const ReleaseManagementPage = lazy(() => import("./pages/portal/enterprise/ReleaseManagementPage"));

// Profile Page
const ProfilePage = lazy(() => import("./pages/portal/ProfilePage"));

// Role-based Dashboards (Correctif 37)
const CandidateDashboardPage = lazy(() => import("./pages/candidate/CandidateDashboard"));
const FreelancerDashboardPage = lazy(() => import("./pages/freelancer/FreelancerDashboard"));
const ManagerDashboardPage = lazy(() => import("./pages/manager/ManagerDashboard"));
const HRDashboardPage = lazy(() => import("./pages/hr/HRDashboard"));
const DirectorDashboardPage = lazy(() => import("./pages/director/DirectorDashboard"));
const ProfileSettingsPage = lazy(() => import("./pages/profile/ProfileSettingsPage"));

// Freelancer Portal (Correctif 64 — Phase 2)
const FreelancerDashboardNewPage = lazy(() => import("./pages/freelancer/FreelancerDashboardNew"));
const FreelancerProposalsPage = lazy(() => import("./pages/freelancer/FreelancerProposalsPage"));
const FreelancerMissionsPage = lazy(() => import("./pages/freelancer/FreelancerMissionsPage"));
const FreelancerPlanningPage = lazy(() => import("./pages/freelancer/FreelancerPlanningPage"));
const FreelancerTrainingPage = lazy(() => import("./pages/freelancer/FreelancerTrainingPage"));
const FreelancerWalletPage = lazy(() => import("./pages/freelancer/FreelancerWalletPage"));
const FreelancerReviewsPage = lazy(() => import("./pages/freelancer/FreelancerReviewsPage"));
const FreelancerProfilePortalPage = lazy(() => import("./pages/freelancer/FreelancerProfilePage"));
const FreelancerSettingsPage = lazy(() => import("./pages/freelancer/FreelancerSettingsPage"));
const FreelancerWorkspacePage = lazy(() => import("./pages/freelancer/FreelancerWorkspacePage"));
const FreelancerMessagesPage = lazy(() => import("./pages/freelancer/FreelancerMessagesPage"));
const SubmitProposalPage = lazy(() => import("./pages/freelancer/SubmitProposalPage"));

// Client Portal (Correctif 64 — Phase 3)
const ClientDashboardNewPage = lazy(() => import("./pages/client/ClientDashboardPage"));
const ClientMissionsPage = lazy(() => import("./pages/client/ClientMissionsPage"));
const ClientContractsPage = lazy(() => import("./pages/client/ClientContractsPage"));
const ClientPaymentsNewPage = lazy(() => import("./pages/client/ClientPaymentsPage"));
const ClientReviewsPage = lazy(() => import("./pages/client/ClientReviewsPage"));
const ClientSettingsNewPage = lazy(() => import("./pages/client/ClientSettingsPage"));
const ClientPostMissionPage = lazy(() => import("./pages/client/ClientPostMissionPage"));
const ClientProposalReviewPage = lazy(() => import("./pages/client/ClientProposalReviewPage"));
const ClientContractSigningPage = lazy(() => import("./pages/client/ClientContractSigningPage"));
const ClientMessagesPage = lazy(() => import("./pages/client/ClientMessagesPage"));

// Marketplace Pages (Correctif 69)
const FreelancerMarketplacePage = lazy(() => import("./pages/marketplace/FreelancerMarketplacePage"));
const MissionMarketplacePage = lazy(() => import("./pages/marketplace/MissionMarketplacePage"));
const FreelancerPublicProfilePage = lazy(() => import("./pages/marketplace/FreelancerPublicProfilePage"));
const MissionDetailPublicPage = lazy(() => import("./pages/marketplace/MissionDetailPublicPage"));

// Freelance Marketplace (Correctif 39)
const FindFreelancerPage = lazy(() => import("./pages/freelance/FindFreelancer"));
const FreelanceOrderWizardPage = lazy(() => import("./pages/freelance/OrderWizard"));
const FreelancerProfilePage = lazy(() => import("./pages/freelance/FreelancerProfile"));
const FreelancerCategoryPage = lazy(() => import("./pages/FreelancerCategoryPage"));
const SuperAdminDashboardPage = lazy(() => import("./pages/admin/SuperAdminDashboard"));
// Admin Phase 4 — Escrow, Disputes, Invoices, Commission
const AdminEscrowPage = lazy(() => import("./pages/admin/AdminEscrowPage"));
const AdminDisputesPage = lazy(() => import("./pages/admin/AdminDisputesPage"));
const AdminPlatformInvoicesPage = lazy(() => import("./pages/admin/AdminPlatformInvoicesPage"));
const AdminCommissionPage = lazy(() => import("./pages/admin/AdminCommissionPage"));

// Client Portal Control Center Pages
const ClientPortalHome = lazy(() => import("./pages/portal/enterprise/ClientPortalHome"));
const ModificationCenterPage = lazy(() => import("./pages/portal/enterprise/ModificationCenterPage"));
const NewChangeRequestPage = lazy(() => import("./pages/portal/enterprise/NewChangeRequestPage"));
const ClientOrdersPage = lazy(() => import("./pages/portal/enterprise/ClientOrdersPage"));
const BPOProgramsPage = lazy(() => import("./pages/portal/enterprise/BPOProgramsPage"));
const ITProjectsPage = lazy(() => import("./pages/portal/enterprise/ITProjectsPage"));
const ClientSettingsPage = lazy(() => import("./pages/portal/enterprise/ClientSettingsPage"));

// Admin Portal
const AdminDashboard = lazy(() => import("./pages/portal/admin/AdminDashboard"));
const AdminReports = lazy(() => import("./pages/portal/admin/AdminReports"));
const AdminUsers = lazy(() => import("./pages/portal/admin/AdminUsers"));
const AdminTickets = lazy(() => import("./pages/portal/admin/AdminTickets"));
const WorkforceSchedule = lazy(() => import("./pages/portal/admin/WorkforceSchedule"));
const AdminIntegrations = lazy(() => import("./pages/portal/admin/AdminIntegrations"));
const AdminClients = lazy(() => import("./pages/portal/admin/AdminClients"));
const AdminClientDetail = lazy(() => import("./pages/portal/admin/AdminClientDetail"));
const AdminSubscriptions = lazy(() => import("./pages/portal/admin/AdminSubscriptions"));
const AdminActivity = lazy(() => import("./pages/portal/admin/AdminActivity"));
const AdminAgents = lazy(() => import("./pages/portal/admin/AdminAgents"));
const AdminSettings = lazy(() => import("./pages/portal/admin/AdminSettings"));

// Utility Pages
const BookingPage = lazy(() => import("./pages/BookingPage"));
const WhistleblowingPage = lazy(() => import("./pages/WhistleblowingPage"));
const RFPPage = lazy(() => import("./pages/RFPPage"));

// Enterprise Pages
const TrustCenterPage = lazy(() => import("./pages/TrustCenterPage"));
const PricingModelsPage = lazy(() => import("./pages/PricingModelsPage"));
const PartnershipsPage = lazy(() => import("./pages/PartnershipsPage"));
// FAQPage moved to Part 5 imports above
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const QuickStartPage = lazy(() => import("./pages/QuickStartPage"));
const NewsroomPage = lazy(() => import("./pages/NewsroomPage"));
const ProofPage = lazy(() => import("./pages/ProofPage"));

// New Enterprise Pages
const FactSheetPage = lazy(() => import("./pages/FactSheetPage"));
const ESGPage = lazy(() => import("./pages/ESGPage"));
const ESGGovernancePage = lazy(() => import("./pages/esg/ESGGovernancePage"));
const ESGPeoplePage = lazy(() => import("./pages/esg/ESGPeoplePage"));
const ESGCommunityPage = lazy(() => import("./pages/esg/ESGCommunityPage"));
const RecognitionsPage = lazy(() => import("./pages/RecognitionsPage"));
const MetricsLibraryPage = lazy(() => import("./pages/MetricsLibraryPage"));

// Product Pages
const NeuralAgentPage = lazy(() => import("./pages/products/NeuralAgentPage"));

// Solutions Pages
const SolutionsHubPage = lazy(() => import("./pages/solutions/SolutionsHubPage"));
const CustomerExperiencePage = lazy(() => import("./pages/solutions/CustomerExperiencePage"));
const BPOContactCenterPage = lazy(() => import("./pages/solutions/BPOContactCenterPage"));
const ProcurementBPOPage = lazy(() => import("./pages/services/ProcurementBPOPage"));
const DigitalFactoryPage = lazy(() => import("./pages/solutions/DigitalFactoryPage"));
const TrustAndSafetyPage = lazy(() => import("./pages/solutions/TrustAndSafetyPage"));
const BestInClassAppsPage = lazy(() => import("./pages/solutions/BestInClassAppsPage"));
const ReferenceArchitecturePage = lazy(() => import("./pages/solutions/ReferenceArchitecturePage"));
const RoutingPlaybookPage = lazy(() => import("./pages/solutions/RoutingPlaybookPage"));

const ServiceBundleLandingPage = lazy(() => import("./pages/ServiceBundleLandingPage"));
const ProcessLandingPage = lazy(() => import("./pages/ProcessLandingPage"));

const IndustryPage = lazy(() => import("./pages/industries/IndustryPage"));

// Trust Pages (Enterprise Governance)
const TrustModelPage = lazy(() => import("./pages/trust/TrustModelPage"));
const DataRightsPage = lazy(() => import("./pages/trust/DataRightsPage"));
const SecurityAssurancePage = lazy(() => import("./pages/trust/SecurityAssurancePage"));
const AIGovernancePage = lazy(() => import("./pages/trust/AIGovernancePage"));
const SecurityCulturePage = lazy(() => import("./pages/trust/SecurityCulturePage"));

// Capability Pages
const WorkforceManagementPage = lazy(() => import("./pages/capabilities/WorkforceManagementPage"));
const QualityAssurancePage = lazy(() => import("./pages/capabilities/QualityAssurancePage"));
const KnowledgeManagementPage = lazy(() => import("./pages/capabilities/KnowledgeManagementPage"));
const ReportingBIPage = lazy(() => import("./pages/capabilities/ReportingBIPage"));
const TrainingOnboardingPage = lazy(() => import("./pages/capabilities/TrainingOnboardingPage"));
const ContinuousImprovementPage = lazy(() => import("./pages/capabilities/ContinuousImprovementPage"));

// Digital Factory Engineering Pages
const DevSecOpsPage = lazy(() => import("./pages/services/digital/DevSecOpsPage"));
const ReliabilityObservabilityPage = lazy(() => import("./pages/services/digital/ReliabilityObservabilityPage"));

// Maturity Assessment Tool
const MaturityAssessmentPage = lazy(() => import("./pages/tools/MaturityAssessmentPage"));

// Location Pages
const LocationsPage = lazy(() => import("./pages/locations/LocationsPage"));
const MoroccoPage = lazy(() => import("./pages/locations/MoroccoPage"));
const SpainPage = lazy(() => import("./pages/locations/SpainPage"));

// Legal Pages
const AccessibilityPage = lazy(() => import("./pages/legal/AccessibilityPage"));

// Order Now & Portal Pages
const OrderWizardPage = lazy(() => import("./pages/start/OrderWizardPage"));
const OrderConfirmationPage = lazy(() => import("./pages/start/OrderConfirmationPage"));
const ClientPortalDashboard = lazy(() => import("./pages/portal/client/ClientPortalDashboard"));

// Enterprise Portal Pages
const BPODashboard = lazy(() => import("./pages/portal/enterprise/BPODashboard"));
const ITDashboard = lazy(() => import("./pages/portal/enterprise/ITDashboard"));
const DeploymentTracker = lazy(() => import("./pages/portal/enterprise/DeploymentTracker"));
const LiveOpsDashboard = lazy(() => import("./pages/portal/enterprise/LiveOpsDashboard"));
const ChangeRequestsPage = lazy(() => import("./pages/portal/enterprise/ChangeRequestsPage"));
const AutomationEnginePage = lazy(() => import("./pages/portal/enterprise/AutomationEnginePage"));
const WFMCenterPage = lazy(() => import("./pages/portal/enterprise/WFMCenterPage"));
const QMCenterPage = lazy(() => import("./pages/portal/enterprise/QMCenterPage"));
const KnowledgeBasePage = lazy(() => import("./pages/portal/enterprise/KnowledgeBasePage"));
const ITSMTicketsPage = lazy(() => import("./pages/portal/enterprise/ITSMTicketsPage"));
const ObservabilityHubPage = lazy(() => import("./pages/portal/enterprise/ObservabilityHubPage"));
const BillingPage = lazy(() => import("./pages/portal/enterprise/BillingPage"));

// New Enterprise Portal Pages
const IntegrationCenterPage = lazy(() => import("./pages/portal/enterprise/IntegrationCenterPage"));
const BillingEnginePage = lazy(() => import("./pages/portal/enterprise/BillingEnginePage"));
const AlertCenterPage = lazy(() => import("./pages/portal/enterprise/AlertCenterPage"));
const SecuritySettingsPage = lazy(() => import("./pages/portal/enterprise/SecuritySettingsPage"));
const GovernanceSLAPage = lazy(() => import("./pages/portal/enterprise/GovernanceSLAPage"));
const GovernancePlaybooksPage = lazy(() => import("./pages/portal/enterprise/GovernancePlaybooksPage"));
const ChangeManagementPage = lazy(() => import("./pages/portal/enterprise/ChangeManagementPage"));

// Portal Services & Order Pages
const PortalServicesPage = lazy(() => import("./pages/portal/enterprise/PortalServicesPage"));
const ITOrderWizardPage = lazy(() => import("./pages/portal/enterprise/ITOrderWizardPage"));
const BPOOrderWizardPage = lazy(() => import("./pages/portal/enterprise/BPOOrderWizardPage"));
const UnifiedTrackerPage = lazy(() => import("./pages/portal/enterprise/UnifiedTrackerPage"));

// CRM Portal Pages (Knowledge Base, Requests)
const PortalKnowledgeBasePage = lazy(() => import("./pages/portal/kb/KnowledgeBasePage"));
const RequestsListPage = lazy(() => import("./pages/portal/requests/RequestsListPage"));
const NewRequestPage = lazy(() => import("./pages/portal/requests/NewRequestPage"));
const RequestDetailPage = lazy(() => import("./pages/portal/requests/RequestDetailPage"));

// Telephony Center Pages
const TelephonyCenterPage = lazy(() => import("./pages/portal/voice/TelephonyCenterPage"));
const QueuesPage = lazy(() => import("./pages/portal/voice/QueuesPage"));
const VoiceCampaignsPage = lazy(() => import("./pages/portal/voice/CampaignsPage"));
const LeadsPage = lazy(() => import("./pages/portal/voice/LeadsPage"));
const RecordingsPage = lazy(() => import("./pages/portal/voice/RecordingsPage"));
const VoiceMonitoringPage = lazy(() => import("./pages/portal/voice/MonitoringPage"));
const VoiceReportsPage = lazy(() => import("./pages/portal/voice/ReportsPage"));
const VoiceIntegrationsPage = lazy(() => import("./pages/portal/voice/IntegrationsPage"));

// SIP Management Pages
const SipTrunksPage = lazy(() => import("./pages/portal/voice/sip/SipTrunksPage"));
const SipWizardPage = lazy(() => import("./pages/portal/voice/sip/SipWizardPage"));
const SipDetailPage = lazy(() => import("./pages/portal/voice/sip/SipDetailPage"));
const SipTestPage = lazy(() => import("./pages/portal/voice/sip/SipTestPage"));
const SipIntegrationsPage = lazy(() => import("./pages/portal/voice/sip/SipIntegrationsPage"));
const DIDsPage = lazy(() => import("./pages/portal/voice/DIDsPage"));
const OutboundRoutesPage = lazy(() => import("./pages/portal/voice/OutboundRoutesPage"));

// Service × Country programmatic SEO pages
const ServiceByCountryPage = lazy(() => import("./pages/ServiceByCountryPage"));
const SERVICE_BY_COUNTRY_PREFIXES = ['bpo-services', 'it-development', 'web-development', 'digital-marketing'];
const SERVICE_BY_COUNTRY_COUNTRIES = ['france','uk','germany','usa','spain','italy','canada','australia','uae','netherlands','belgium','switzerland','portugal','sweden','denmark','norway','ireland','austria','japan','south-korea','singapore','saudi-arabia','qatar','india','brazil','mexico','poland','romania','greece','turkey','south-africa','nigeria','egypt','colombia','argentina','philippines','malaysia','thailand','vietnam','indonesia'];

// Service × City programmatic SEO pages
const ServiceByCityPage = lazy(() => import("./pages/ServiceByCityPage"));
const SERVICE_BY_CITY_PREFIXES = ['bpo-services', 'it-development', 'web-development', 'digital-marketing', 'call-center'];
const SERVICE_BY_CITY_CITIES = ['paris','london','berlin','madrid','amsterdam','brussels','zurich','milan','stockholm','oslo','copenhagen','lisbon','warsaw','dubai','abu-dhabi','new-york','los-angeles','miami','toronto','montreal','sydney','singapore','tokyo','seoul','hong-kong','shanghai','mumbai','bangalore','riyadh','johannesburg','casablanca','sao-paulo','mexico-city','bogota','buenos-aires','vienna','prague','budapest','bucharest','helsinki','barcelona','lyon','frankfurt','munich','manchester','chicago','houston','istanbul','nairobi','lagos'];

// Communications Center Pages
const CommunicationsCenterPage = lazy(() => import("./pages/portal/mail/CommunicationsCenterPage"));
const EmailCampaignsPage = lazy(() => import("./pages/portal/mail/EmailCampaignsPage"));
const TransactionalPage = lazy(() => import("./pages/portal/mail/TransactionalPage"));
const ListsPage = lazy(() => import("./pages/portal/mail/ListsPage"));
const TemplatesPage = lazy(() => import("./pages/portal/mail/TemplatesPage"));
const DeliverabilityPage = lazy(() => import("./pages/portal/mail/DeliverabilityPage"));
const SuppressionsPage = lazy(() => import("./pages/portal/mail/SuppressionsPage"));
const MailLogsPage = lazy(() => import("./pages/portal/mail/LogsPage"));
const MailAutomationPage = lazy(() => import("./pages/portal/mail/AutomationPage"));
const MailReportsPage = lazy(() => import("./pages/portal/mail/ReportsPage"));
const PortalEmailServersPage = lazy(() => import("./pages/portal/mail/EmailServersPage"));

// Admin Telephony & Mail Pages
const VoiceAdminPage = lazy(() => import("./pages/admin/VoiceAdminPage"));
const MailAdminPage = lazy(() => import("./pages/admin/MailAdminPage"));
const AdminVoiceDIDsPage = lazy(() => import("./pages/admin/AdminVoiceDIDsPage"));
const AdminVoiceMonitoringPage = lazy(() => import("./pages/admin/AdminVoiceMonitoringPage"));
const AdminVoiceLogsPage = lazy(() => import("./pages/admin/AdminVoiceLogsPage"));
const AdminMailCampaignsPage = lazy(() => import("./pages/admin/AdminMailCampaignsPage"));
const AdminMailDomainsPage = lazy(() => import("./pages/admin/AdminMailDomainsPage"));
const AdminMailLogsPage = lazy(() => import("./pages/admin/AdminMailLogsPage"));
const AdminMailWebhooksPage = lazy(() => import("./pages/admin/AdminMailWebhooksPage"));
const AdminDeliverabilityDashboardPage = lazy(() => import("./pages/admin/AdminDeliverabilityDashboardPage"));
const AdminIPWarmupPage = lazy(() => import("./pages/admin/AdminIPWarmupPage"));
const PreferenceCenterPage = lazy(() => import("./pages/portal/mail/PreferenceCenterPage"));

// CRM Pages
const CRMContactsPage = lazy(() => import("./pages/admin/CRMContactsPage"));
const CRMCompaniesPage = lazy(() => import("./pages/admin/CRMCompaniesPage"));
const CRMDealsPage = lazy(() => import("./pages/admin/CRMDealsPage"));
const CampaignBuilderPage = lazy(() => import("./pages/admin/CampaignBuilderPage"));
const CRMInboxPage = lazy(() => import("./pages/admin/CRMInboxPage"));

const AuditReportPage = lazy(() => import("./pages/admin/AuditReportPage"));
const AdminStaffingPage = lazy(() => import("./pages/admin/AdminStaffingPage"));
const AdminDataRetentionPage = lazy(() => import("./pages/admin/AdminDataRetentionPage"));
const AdminAuditLogsPage = lazy(() => import("./pages/admin/AdminAuditLogsPage"));
const AdminAlertsPage = lazy(() => import("./pages/admin/AdminAlertsPage"));
const AdminIncidentsPage = lazy(() => import("./pages/admin/AdminIncidentsPage"));
const AdminObservabilityPage = lazy(() => import("./pages/admin/AdminObservabilityPage"));
const AdminReleasesPage = lazy(() => import("./pages/admin/AdminReleasesPage"));
const AdminTrainingPage = lazy(() => import("./pages/admin/AdminTrainingPage"));
const AdminAPIKeysPage = lazy(() => import("./pages/admin/AdminAPIKeysPage"));
const AdminWebhooksPage = lazy(() => import("./pages/admin/AdminWebhooksPage"));
const AdminAccessReviewsPage = lazy(() => import("./pages/admin/AdminAccessReviewsPage"));
const AdminMailEnginesPage = lazy(() => import("./pages/admin/AdminMailEnginesPage"));
const EmailServersPage = lazy(() => import("./pages/admin/mail/EmailServersPage"));
const AdminInvoicesPage = lazy(() => import("./pages/admin/billing/AdminBillingInvoicesPage"));
const AdminUsagePage = lazy(() => import("./pages/admin/billing/AdminBillingUsagePage"));
const AdminActivityLogPage = lazy(() => import("./pages/admin/AdminActivityLogPage"));
const AdminSecurityPoliciesPageNew = lazy(() => import("./pages/admin/security/AdminSecurityPoliciesPage"));
const AdminSSOPage = lazy(() => import("./pages/admin/AdminSSOPage"));
const AdminServiceCatalogPage = lazy(() => import("./pages/admin/AdminServiceCatalogPage"));
const AdminAutomationPage = lazy(() => import("./pages/admin/AdminAutomationPage"));
const AdminIntegrationLogsPage = lazy(() => import("./pages/admin/AdminIntegrationLogsPage"));

// New CCaaS Core Modules
const AgentDesktopPage = lazy(() => import("./pages/portal/agent/AgentDesktopPage"));
const SupervisorCommandCenterPage = lazy(() => import("./pages/admin/SupervisorCommandCenterPage"));
const SimulationModePage = lazy(() => import("./pages/admin/SimulationModePage"));
const CRMCasesPage = lazy(() => import("./pages/admin/CRMCasesPage"));
const SalesCampaignsPage = lazy(() => import("./pages/admin/SalesCampaignsPage"));
const AnalyticsDashboardPage = lazy(() => import("./pages/admin/AnalyticsDashboardPage"));
const RoutingEnginePage = lazy(() => import("./pages/admin/RoutingEnginePage"));
const KnowledgeBaseAdminPage = lazy(() => import("./pages/admin/KnowledgeBaseAdminPage"));

// WFM, QA, Compliance Modules
const AdminWFMSchedulingPage = lazy(() => import("./pages/admin/AdminWFMSchedulingPage"));
const AdminQAScorecardsPage = lazy(() => import("./pages/admin/AdminQAScorecardsPage"));
const AdminCoachingTasksPage = lazy(() => import("./pages/admin/AdminCoachingTasksPage"));
const AdminDataCompliancePage = lazy(() => import("./pages/admin/AdminDataCompliancePage"));
const AdminTenantManagementPage = lazy(() => import("./pages/admin/AdminTenantManagementPage"));
const AdminRecordingsPage = lazy(() => import("./pages/admin/AdminRecordingsPage"));

// Connector & Health Modules
const AdminConnectorsPage = lazy(() => import("./pages/admin/AdminConnectorsPage"));
const AdminHealthEndpointsPage = lazy(() => import("./pages/admin/AdminHealthEndpointsPage"));
const AdminE2ETestsPage = lazy(() => import("./pages/admin/AdminE2ETestsPage"));
const AdminStatusPage = lazy(() => import("./pages/admin/AdminStatusPage"));
const AZChecklistPage = lazy(() => import("./pages/admin/AZChecklistPage"));
const AdminSEOAnalyticsPage = lazy(() => import("./pages/admin/AdminSEOAnalyticsPage"));
const AdminSEOAuditPage = lazy(() => import("./pages/admin/AdminSEOAuditPage"));
const AdminGSCMonitorPage = lazy(() => import("./pages/admin/AdminGSCMonitorPage"));
const AdminOGInspectorPage = lazy(() => import("./pages/admin/AdminOGInspectorPage"));

// Insights & Analytics Page (What Changed?)
const InsightsAnalyticsPage = lazy(() => import("./pages/portal/InsightsAnalyticsPage"));

// Global Components — lazy-loaded (not needed for initial paint)
const BackToTop = lazy(() => import("@/components/BackToTop").then(m => ({ default: m.BackToTop })));
const GlobalBookingModal = lazy(() => import("@/components/GlobalBookingModal").then(m => ({ default: m.GlobalBookingModal })));

const queryClient = new QueryClient();

// Fast skeleton loader — visible immediately, SEO content for crawlers
const PageLoader = () => (
  <div className="min-h-screen bg-white">
    {/* Visible skeleton header */}
    <div className="px-6 py-4 flex items-center gap-2 border-b border-white/5">
      <Logo variant="full" size="md" darkMode={true} />
    </div>
    {/* Skeleton content blocks */}
    <div className="max-w-4xl mx-auto px-6 py-16 space-y-6">
      <div className="h-10 w-3/4 bg-white/5 rounded animate-pulse" />
      <div className="h-6 w-full bg-white/5 rounded animate-pulse" />
      <div className="h-6 w-2/3 bg-white/5 rounded animate-pulse" />
      <div className="flex gap-4 pt-4">
        <div className="h-12 w-40 bg-cyan/10 rounded-lg animate-pulse" />
        <div className="h-12 w-40 bg-white/5 rounded-lg animate-pulse" />
      </div>
    </div>
    {/* SEO content for crawlers (sr-only) */}
    <div className="sr-only">
      <h1>Enterprise BPO &amp; Digital Factory — BPO Contact</h1>
      <p>24/7 multilingual customer support, call center outsourcing, web &amp; mobile development, SaaS, AI solutions from Casablanca (Morocco).</p>
      <nav aria-label="Quick links">
        <a href="/en/services">Services</a>
        <a href="/en/services/bpo">BPO</a>
        <a href="/en/services/digital-studio">Digital Studio</a>
        <a href="/en/pricing">Pricing</a>
        <a href="/en/contact">Contact</a>
        <a href="/en/about">About</a>
      </nav>
    </div>
  </div>
);

// Language-aware redirect helper (preserves /:lang prefix)
// CTA-ROUTING-1710b: /quote is a commercial quote request -> single order flow, keeping lang + query.
const QuoteToStartRedirect = () => {
  const { lang } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${lang || 'en'}/start${search}`} replace />;
};

// PILOT-1810h: retired pilot pages -> BPO quote request with tracking source (never a page label).
const PilotToStartRedirect = ({ from }: { from: string }) => {
  const { lang } = useParams();
  return <Navigate to={`/${lang || 'en'}/start?type=bpo&from=${from}&cta_id=${from}`} replace />;
};

// Keeps query (from/cta_id/utm) when an alias path points to its canonical page.
const LangRedirectKeepQuery = ({ to }: { to: string }) => {
  const { lang } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${lang || 'en'}${to}${search}${hash}`} replace />;
};

const LangRedirect = ({ to }: { to: string }) => {
  const { lang } = useParams();
  return <Navigate to={`/${lang || 'en'}${to}`} replace />;
};

// Redirect /insights/:slug to /blog/:slug preserving language
const InsightsSlugRedirect = () => {
  const { lang, slug } = useParams();
  return <Navigate to={`/${lang || 'en'}/blog/${slug || ''}`} replace />;
};

// ── SEO-supported languages (all 29 get indexed) ──
const SEO_SUPPORTED_LANGS = new Set(['en','fr','es','ar','de','it','pt','nl','pl','ru','zh','ja','ko','th','tr','vi','el','sv','da','fi','no','hu','cs','ro','id','ms','tl','bn','hi']);

// Localized routes wrapped with LanguageProvider
const LocalizedRoutes = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading, role, isInternalStaff } = useAuth();
  useEffect(() => { rememberRoute(location.pathname); }, [location.pathname]);

  const langFromPath = location.pathname.split('/')[1] || 'en';

  // FIX 1: Redirect unsupported language prefixes to /en/ equivalent
  // Use window.location.replace for proper 301-equivalent redirect (React Navigate is client-only)
  if (langFromPath.length === 2 && /^[a-z]{2}$/.test(langFromPath) && !SEO_SUPPORTED_LANGS.has(langFromPath)) {
    const restOfPath = location.pathname.replace(/^\/[^/]+/, '');
    if (typeof window !== 'undefined') {
      window.location.replace(`/en${restOfPath || '/'}`);
    }
    return null; // Render nothing while redirecting
  }
  // Unknown first segment (not a language): show the 404 page, never the homepage.
  if (!SEO_SUPPORTED_LANGS.has(langFromPath)) {
    return <Suspense fallback={null}><NotFoundPage /></Suspense>;
  }
  const isPortalPath = /^\/[^/]+\/portal(?:\/|$)/.test(location.pathname);

  // Strict separation: internal/staff must never access /:lang/portal/*
  if (isPortalPath) {
    if (isLoading) return <PageLoader />;

    if (!isAuthenticated) {
      return <Navigate to={`/${langFromPath}/auth`} replace />;
    }

    if (isInternalStaff) {
      return <Navigate to="/admin/dashboard" replace />;
    }

    // Only clients can use the client portal
    if (role !== 'client') {
      const redirect = role === 'agent'
        ? `/${langFromPath}/agent`
        : '/admin/dashboard';
      return <Navigate to={redirect} replace />;
    }
  }

  return (
    <LanguageProvider>
      <Routes>
      <Route index element={<HomePage />} />
      
      {/* Services Catalogue & Configurators */}
      <Route path="nos-services" element={<LangRedirect to="/services" />} />
      <Route path="nos-services/assistance-technique" element={<LangRedirect to="/services/bpo/technical-support" />} />
      <Route path="nos-services/support-client" element={<LangRedirect to="/services/bpo/inbound-customer-care" />} />
      <Route path="nos-services/ventes-sortantes" element={<LangRedirect to="/services/bpo/outbound-sales" />} />
      <Route path="nos-services/enquetes-satisfaction" element={<LangRedirect to="/services/bpo/market-research-surveys" />} />
      <Route path="nos-services/strategie-digitale" element={<LangRedirect to="/services/digital-studio/digital-transformation" />} />
      <Route path="nos-services/formation-equipe" element={<LangRedirect to="/capabilities/training-onboarding" />} />
      <Route path="nos-services/cybersecurite" element={<LangRedirect to="/services/digital-studio/cybersecurity-appsec" />} />
      <Route path="nos-services/application-web" element={<LangRedirect to="/services/digital-studio/custom-saas" />} />
      <Route path="nos-services/refonte-site" element={<LangRedirect to="/services/digital-studio/legacy-modernization" />} />
      <Route path="nos-services/e-commerce" element={<LangRedirect to="/services/digital-studio/ecommerce" />} />
      <Route path="nos-services/hebergement-infogere" element={<LangRedirect to="/services/digital-studio/managed-services" />} />
      <Route path="nos-services/support-client-24-7" element={<LangRedirect to="/services/bpo/inbound-customer-care" />} />
      <Route path="nos-services/site-vitrine" element={<LangRedirect to="/services/digital-studio/web-development" />} />
      <Route path="nos-services/campagne-telephonique" element={<LangRedirect to="/services/bpo/outbound-sales" />} />
      <Route path="nos-services/*" element={<LangRedirect to="/services" />} />
      <Route path="checkout" element={<CheckoutPage />} />
      <Route path="tarifs" element={<ShopPricingPage />} />
      
      {/* Public Hub Pages */}
      <Route path="services" element={<ServicesPage />} />
      <Route path="services/bpo" element={<BPOServicesPage />} />
      <Route path="services/bpo/call-center" element={<CallCenterServicePage />} />
      <Route path="services/digital-studio" element={<DigitalStudioPage />} />
      <Route path="industries" element={<IndustriesPageHub />} />
      <Route path="case-studies" element={<CaseStudiesPage />} />
      <Route path="case-studies/healthcare-bpo-transformation" element={<CaseStudyDetailPage />} />
      <Route path="case-studies/fintech-customer-support-scaling" element={<CaseStudyDetailPage />} />
      <Route path="case-studies/ecommerce-multilingual-expansion" element={<CaseStudyDetailPage />} />
      <Route path="case-studies/telecom-technical-support-optimization" element={<CaseStudyDetailPage />} />
      <Route path="case-studies/saas-virtual-assistant-deployment" element={<CaseStudyDetailPage />} />
      <Route path="case-studies/:slug" element={<CaseStudyDetailPage />} />
      <Route path="testimonials" element={<TestimonialsPage />} />
      <Route path="why-morocco" element={<WhyMoroccoPage />} />
      <Route path="ai-contact-center" element={<AIContactCenterPage />} />
      <Route path="sitemap-index" element={<SitemapIndexPage />} />
      {/* Geo-targeted country landing pages (English + translated slugs) */}
      {ALL_COUNTRY_ROUTE_SLUGS.map(slug => (
        <Route key={slug} path={slug} element={<CountryLandingPage />} />
      ))}
      {/* Geo-targeted country agency landing pages (English + translated slugs) */}
      {ALL_AGENCY_ROUTE_SLUGS.map(slug => (
        <Route key={`agency-${slug}`} path={slug} element={<CountryAgencyLandingPage />} />
      ))}
      {/* Continent landing pages (legacy bpo-* slugs) */}
      <Route path="bpo-europe" element={<ContinentLandingPage />} />
      <Route path="bpo-north-america" element={<ContinentLandingPage />} />
      <Route path="bpo-latin-america" element={<ContinentLandingPage />} />
      <Route path="bpo-africa" element={<ContinentLandingPage />} />
      <Route path="bpo-asia" element={<ContinentLandingPage />} />
      <Route path="bpo-middle-east" element={<ContinentLandingPage />} />
      <Route path="bpo-oceania" element={<ContinentLandingPage />} />
      {/* NEW: Region landing pages — 26 regions via call-center-{region} */}
      <Route path="call-center-europe" element={<RegionLandingPage />} />
      <Route path="call-center-asia" element={<RegionLandingPage />} />
      <Route path="call-center-africa" element={<RegionLandingPage />} />
      <Route path="call-center-americas" element={<RegionLandingPage />} />
      <Route path="call-center-north-america" element={<RegionLandingPage />} />
      <Route path="call-center-south-america" element={<RegionLandingPage />} />
      <Route path="call-center-central-america" element={<RegionLandingPage />} />
      <Route path="call-center-caribbean" element={<RegionLandingPage />} />
      <Route path="call-center-oceania" element={<RegionLandingPage />} />
      <Route path="call-center-middle-east" element={<RegionLandingPage />} />
      <Route path="call-center-southeast-asia" element={<RegionLandingPage />} />
      <Route path="call-center-east-asia" element={<RegionLandingPage />} />
      <Route path="call-center-south-asia" element={<RegionLandingPage />} />
      <Route path="call-center-central-asia" element={<RegionLandingPage />} />
      <Route path="call-center-eastern-europe" element={<RegionLandingPage />} />
      <Route path="call-center-western-europe" element={<RegionLandingPage />} />
      <Route path="call-center-northern-europe" element={<RegionLandingPage />} />
      <Route path="call-center-southern-europe" element={<RegionLandingPage />} />
      <Route path="call-center-west-africa" element={<RegionLandingPage />} />
      <Route path="call-center-east-africa" element={<RegionLandingPage />} />
      <Route path="call-center-north-africa" element={<RegionLandingPage />} />
      <Route path="call-center-southern-africa" element={<RegionLandingPage />} />
      <Route path="call-center-gulf-states" element={<RegionLandingPage />} />
      <Route path="call-center-nordic-countries" element={<RegionLandingPage />} />
      <Route path="call-center-latin-america" element={<RegionLandingPage />} />
      <Route path="call-center-pacific-islands" element={<RegionLandingPage />} />
      {/* Call Center City Pages — Batch 1 */}
      <Route path="call-center-new-york" element={<CallCenterCityPage />} />
      <Route path="call-center-london" element={<CallCenterCityPage />} />
      <Route path="call-center-paris" element={<CallCenterCityPage />} />
      <Route path="call-center-berlin" element={<CallCenterCityPage />} />
      <Route path="call-center-tokyo" element={<CallCenterCityPage />} />
      <Route path="call-center-sydney" element={<CallCenterCityPage />} />
      <Route path="call-center-toronto" element={<CallCenterCityPage />} />
      <Route path="call-center-dubai" element={<CallCenterCityPage />} />
      <Route path="call-center-singapore" element={<CallCenterCityPage />} />
      <Route path="call-center-hong-kong" element={<CallCenterCityPage />} />
      <Route path="call-center-sao-paulo" element={<CallCenterCityPage />} />
      <Route path="call-center-mexico-city" element={<CallCenterCityPage />} />
      <Route path="call-center-mumbai" element={<CallCenterCityPage />} />
      <Route path="call-center-manila" element={<CallCenterCityPage />} />
      <Route path="call-center-casablanca" element={<CallCenterCityPage />} />
      {/* Call Center City Pages — Batch 2 */}
      <Route path="call-center-bangalore" element={<CallCenterCityPage />} />
      <Route path="call-center-cairo" element={<CallCenterCityPage />} />
      <Route path="call-center-johannesburg" element={<CallCenterCityPage />} />
      <Route path="call-center-lagos" element={<CallCenterCityPage />} />
      <Route path="call-center-nairobi" element={<CallCenterCityPage />} />
      <Route path="call-center-madrid" element={<CallCenterCityPage />} />
      <Route path="call-center-amsterdam" element={<CallCenterCityPage />} />
      <Route path="call-center-zurich" element={<CallCenterCityPage />} />
      <Route path="call-center-stockholm" element={<CallCenterCityPage />} />
      <Route path="call-center-warsaw" element={<CallCenterCityPage />} />
      <Route path="call-center-seoul" element={<CallCenterCityPage />} />
      <Route path="call-center-jakarta" element={<CallCenterCityPage />} />
      <Route path="call-center-buenos-aires" element={<CallCenterCityPage />} />
      <Route path="call-center-lima" element={<CallCenterCityPage />} />
      <Route path="call-center-bogota" element={<CallCenterCityPage />} />
      {/* Call Center City Pages — Batch 3 (Factory-generated, 70 additional cities) */}
      <Route path="call-center-barcelona" element={<CallCenterCityPage />} />
      <Route path="call-center-rome" element={<CallCenterCityPage />} />
      <Route path="call-center-milan" element={<CallCenterCityPage />} />
      <Route path="call-center-brussels" element={<CallCenterCityPage />} />
      <Route path="call-center-vienna" element={<CallCenterCityPage />} />
      <Route path="call-center-geneva" element={<CallCenterCityPage />} />
      <Route path="call-center-lisbon" element={<CityBPOLandingPage />} />
      <Route path="call-center-prague" element={<CityBPOLandingPage />} />
      <Route path="call-center-bucharest" element={<CityBPOLandingPage />} />
      <Route path="call-center-budapest" element={<CityBPOLandingPage />} />
      <Route path="call-center-copenhagen" element={<CityBPOLandingPage />} />
      <Route path="call-center-helsinki" element={<CityBPOLandingPage />} />
      <Route path="call-center-oslo" element={<CityBPOLandingPage />} />
      <Route path="call-center-dublin" element={<CallCenterCityPage />} />
      <Route path="call-center-athens" element={<CityBPOLandingPage />} />
      <Route path="call-center-moscow" element={<CityBPOLandingPage />} />
      <Route path="call-center-saint-petersburg" element={<CityBPOLandingPage />} />
      <Route path="call-center-istanbul" element={<CityBPOLandingPage />} />
      <Route path="call-center-los-angeles" element={<CallCenterCityPage />} />
      <Route path="call-center-chicago" element={<CallCenterCityPage />} />
      <Route path="call-center-houston" element={<CityBPOLandingPage />} />
      <Route path="call-center-miami" element={<CityBPOLandingPage />} />
      <Route path="call-center-san-francisco" element={<CityBPOLandingPage />} />
      <Route path="call-center-vancouver" element={<CityBPOLandingPage />} />
      <Route path="call-center-montreal" element={<CallCenterCityPage />} />
      <Route path="call-center-rio-de-janeiro" element={<CityBPOLandingPage />} />
      <Route path="call-center-santiago" element={<CityBPOLandingPage />} />
      <Route path="call-center-abu-dhabi" element={<CityBPOLandingPage />} />
      <Route path="call-center-riyadh" element={<CallCenterCityPage />} />
      <Route path="call-center-doha" element={<CityBPOLandingPage />} />
      <Route path="call-center-kuwait-city" element={<CityBPOLandingPage />} />
      <Route path="call-center-jeddah" element={<CityBPOLandingPage />} />
      <Route path="call-center-muscat" element={<CityBPOLandingPage />} />
      <Route path="call-center-amman" element={<CityBPOLandingPage />} />
      <Route path="call-center-beirut" element={<CityBPOLandingPage />} />
      <Route path="call-center-cape-town" element={<CityBPOLandingPage />} />
      <Route path="call-center-accra" element={<CityBPOLandingPage />} />
      <Route path="call-center-dar-es-salaam" element={<CityBPOLandingPage />} />
      <Route path="call-center-addis-ababa" element={<CityBPOLandingPage />} />
      <Route path="call-center-delhi" element={<CityBPOLandingPage />} />
      <Route path="call-center-hyderabad" element={<CityBPOLandingPage />} />
      <Route path="call-center-chennai" element={<CityBPOLandingPage />} />
      <Route path="call-center-kolkata" element={<CityBPOLandingPage />} />
      <Route path="call-center-pune" element={<CityBPOLandingPage />} />
      <Route path="call-center-osaka" element={<CityBPOLandingPage />} />
      <Route path="call-center-beijing" element={<CityBPOLandingPage />} />
      <Route path="call-center-shanghai" element={<CityBPOLandingPage />} />
      <Route path="call-center-guangzhou" element={<CityBPOLandingPage />} />
      <Route path="call-center-shenzhen" element={<CityBPOLandingPage />} />
      <Route path="call-center-taipei" element={<CityBPOLandingPage />} />
      <Route path="call-center-bangkok" element={<CityBPOLandingPage />} />
      <Route path="call-center-kuala-lumpur" element={<CityBPOLandingPage />} />
      <Route path="call-center-ho-chi-minh-city" element={<CityBPOLandingPage />} />
      <Route path="call-center-hanoi" element={<CityBPOLandingPage />} />
      <Route path="call-center-melbourne" element={<CityBPOLandingPage />} />
      <Route path="call-center-auckland" element={<CityBPOLandingPage />} />
      <Route path="call-center-dhaka" element={<CityBPOLandingPage />} />
      <Route path="call-center-karachi" element={<CityBPOLandingPage />} />
      <Route path="call-center-colombo" element={<CityBPOLandingPage />} />
      <Route path="call-center-quito" element={<CityBPOLandingPage />} />
      <Route path="call-center-panama-city" element={<CityBPOLandingPage />} />
      <Route path="call-center-san-jose-cr" element={<CityBPOLandingPage />} />
      <Route path="call-center-guatemala-city" element={<CityBPOLandingPage />} />
      <Route path="call-center-kingston" element={<CityBPOLandingPage />} />
      <Route path="call-center-port-of-spain" element={<CityBPOLandingPage />} />
      <Route path="call-center-tunis" element={<CityBPOLandingPage />} />
      <Route path="call-center-algiers" element={<CityBPOLandingPage />} />
      <Route path="call-center-dakar" element={<CityBPOLandingPage />} />
      <Route path="call-center-kigali" element={<CityBPOLandingPage />} />
      {/* French Service Landing Pages */}
      <Route path="services-centre-appel" element={<FrenchServicePage />} />
      <Route path="developpement-web" element={<FrenchServicePage />} />
      <Route path="externalisation-bpo" element={<FrenchServicePage />} />
      <Route path="support-client-multilingue" element={<FrenchServicePage />} />
      <Route path="transformation-digitale" element={<FrenchServicePage />} />
      {/* Spanish Service Landing Pages */}
      <Route path="servicios-call-center" element={<SpanishServicePage />} />
      <Route path="desarrollo-web" element={<SpanishServicePage />} />
      <Route path="externalizacion-bpo" element={<SpanishServicePage />} />
      <Route path="soporte-cliente-multilingue" element={<SpanishServicePage />} />
      <Route path="transformacion-digital" element={<SpanishServicePage />} />
      {/* Arabic Service Landing Pages */}
      <Route path="khidmat-markaz-ittisalat" element={<ArabicServicePage />} />
      <Route path="tatwir-web" element={<ArabicServicePage />} />
      <Route path="isnad-khariji-bpo" element={<ArabicServicePage />} />
      <Route path="dakm-aamalaa-mutaaddid-allughat" element={<ArabicServicePage />} />
      <Route path="tahwil-raqami" element={<ArabicServicePage />} />
      {/* German Service Landing Pages */}
      <Route path="callcenter-dienstleistungen" element={<GermanServicePage />} />
      <Route path="webentwicklung" element={<GermanServicePage />} />
      <Route path="outsourcing-bpo-de" element={<GermanServicePage />} />
      <Route path="mehrsprachiger-kundensupport" element={<GermanServicePage />} />
      <Route path="digitale-transformation" element={<GermanServicePage />} />
      {/* Portuguese Service Landing Pages */}
      <Route path="servicos-call-center" element={<PortugueseServicePage />} />
      <Route path="desenvolvimento-web" element={<PortugueseServicePage />} />
      <Route path="terceirizacao-bpo" element={<PortugueseServicePage />} />
      <Route path="suporte-cliente-multilingue" element={<PortugueseServicePage />} />
      <Route path="transformacao-digital" element={<PortugueseServicePage />} />
      {/* Italian Service Landing Pages */}
      <Route path="servizi-call-center" element={<ItalianServicePage />} />
      <Route path="sviluppo-web" element={<ItalianServicePage />} />
      <Route path="outsourcing-bpo-it" element={<ItalianServicePage />} />
      <Route path="supporto-clienti-multilingue" element={<ItalianServicePage />} />
      <Route path="trasformazione-digitale" element={<ItalianServicePage />} />
      {/* Dutch Service Landing Pages */}
      <Route path="callcenter-diensten" element={<DutchServicePage />} />
      <Route path="webontwikkeling" element={<DutchServicePage />} />
      <Route path="outsourcing-bpo-nl" element={<DutchServicePage />} />
      <Route path="meertalige-klantenservice" element={<DutchServicePage />} />
      <Route path="digitale-transformatie" element={<DutchServicePage />} />
      {/* Polish Service Landing Pages */}
      <Route path="uslugi-call-center" element={<PolishServicePage />} />
      <Route path="tworzenie-stron-www" element={<PolishServicePage />} />
      <Route path="outsourcing-bpo-pl" element={<PolishServicePage />} />
      <Route path="wielojezyczna-obsluga-klienta" element={<PolishServicePage />} />
      <Route path="transformacja-cyfrowa" element={<PolishServicePage />} />
      {/* Hindi Service Landing Pages */}
      <Route path="call-center-seva" element={<HindiServicePage />} />
      <Route path="web-vikas" element={<HindiServicePage />} />
      <Route path="bahubhashi-sahayata" element={<HindiServicePage />} />
      <Route path="outsourcing-bharat" element={<HindiServicePage />} />
      <Route path="dijital-parivartan" element={<HindiServicePage />} />
      {/* Swahili Service Landing Pages */}
      <Route path="huduma-call-center" element={<SwahiliServicePage />} />
      <Route path="uendelezaji-wavuti" element={<SwahiliServicePage />} />
      <Route path="msaada-lugha-nyingi" element={<SwahiliServicePage />} />
      <Route path="huduma-bpo" element={<SwahiliServicePage />} />
      <Route path="mabadiliko-dijitali" element={<SwahiliServicePage />} />
      {/* Bengali Service Landing Pages */}
      <Route path="call-center-sheba" element={<BengaliServicePage />} />
      <Route path="web-uunnayan" element={<BengaliServicePage />} />
      <Route path="bohubhashi-shohayota" element={<BengaliServicePage />} />
      <Route path="outsourcing-bangladesh" element={<BengaliServicePage />} />
      <Route path="digital-rupantar" element={<BengaliServicePage />} />
      {/* Tagalog Service Landing Pages */}
      <Route path="call-center-serbisyo" element={<TagalogServicePage />} />
      <Route path="pagbuo-ng-web" element={<TagalogServicePage />} />
      <Route path="multilingual-suporta" element={<TagalogServicePage />} />
      <Route path="outsourcing-pilipinas" element={<TagalogServicePage />} />
      <Route path="digital-na-pagbabago" element={<TagalogServicePage />} />
      {/* Turkish Service Landing Pages */}
      <Route path="cagri-merkezi-hizmetleri" element={<TurkishServicePage />} />
      <Route path="web-gelistirme" element={<TurkishServicePage />} />
      <Route path="dis-kaynak-bpo" element={<TurkishServicePage />} />
      <Route path="cok-dilli-musteri-destegi" element={<TurkishServicePage />} />
      <Route path="dijital-donusum" element={<TurkishServicePage />} />
      {/* Japanese Service Landing Pages */}
      <Route path="call-center-service" element={<JapaneseServicePage />} />
      <Route path="web-kaihatsu" element={<JapaneseServicePage />} />
      <Route path="outsourcing-bpo-jp" element={<JapaneseServicePage />} />
      <Route path="tagengo-support" element={<JapaneseServicePage />} />
      <Route path="digital-henkan" element={<JapaneseServicePage />} />
      {/* Korean Service Landing Pages */}
      <Route path="call-center-seobiseu" element={<KoreanServicePage />} />
      <Route path="web-gaebal" element={<KoreanServicePage />} />
      <Route path="outsourcing-bpo-ko" element={<KoreanServicePage />} />
      <Route path="dagugeo-jiwon" element={<KoreanServicePage />} />
      <Route path="dijiteol-jeonhwan" element={<KoreanServicePage />} />
      {/* Chinese Service Landing Pages */}
      <Route path="hulljiao-zhongxin-fuwu" element={<ChineseServicePage />} />
      <Route path="wangzhan-kaifa" element={<ChineseServicePage />} />
      <Route path="waibao-bpo" element={<ChineseServicePage />} />
      <Route path="duoyuyan-kehu-zhichi" element={<ChineseServicePage />} />
      <Route path="shuzi-zhuanxing" element={<ChineseServicePage />} />
      {/* Indonesian Service Landing Pages */}
      <Route path="layanan-call-center" element={<IndonesianServicePage />} />
      <Route path="pengembangan-web" element={<IndonesianServicePage />} />
      <Route path="outsourcing-bpo-id" element={<IndonesianServicePage />} />
      <Route path="dukungan-pelanggan-multibahasa" element={<IndonesianServicePage />} />
      <Route path="transformasi-digital" element={<IndonesianServicePage />} />
      <Route path="dukungan-multibahasa" element={<IndonesianServicePage />} />
      {/* Malay Service Landing Pages */}
      <Route path="perkhidmatan-pusat-panggilan" element={<MalayServicePage />} />
      <Route path="pembangunan-web" element={<MalayServicePage />} />
      <Route path="penyumberan-luar-bpo" element={<MalayServicePage />} />
      <Route path="sokongan-pelanggan-pelbagai-bahasa" element={<MalayServicePage />} />
      <Route path="transformasi-digital-ms" element={<MalayServicePage />} />
      <Route path="pusat-panggilan" element={<MalayServicePage />} />
      <Route path="sokongan-pelbagai-bahasa" element={<MalayServicePage />} />
      {/* Thai Service Landing Pages */}
      <Route path="borikar-soon-riak" element={<ThaiServicePage />} />
      <Route path="pattana-web" element={<ThaiServicePage />} />
      <Route path="outsourcing-bpo-th" element={<ThaiServicePage />} />
      <Route path="sapport-lahk-phasa" element={<ThaiServicePage />} />
      <Route path="kan-plian-digital" element={<ThaiServicePage />} />
      <Route path="borikar-call-center" element={<ThaiServicePage />} />
      <Route path="phattana-website" element={<ThaiServicePage />} />
      <Route path="sapport-lak-phasa" element={<ThaiServicePage />} />
      <Route path="kan-plian-dijital" element={<ThaiServicePage />} />
      {/* Vietnamese Service Landing Pages */}
      <Route path="dich-vu-call-center" element={<VietnameseServicePage />} />
      <Route path="phat-trien-web" element={<VietnameseServicePage />} />
      <Route path="outsourcing-bpo-vi" element={<VietnameseServicePage />} />
      <Route path="ho-tro-khach-hang-da-ngon-ngu" element={<VietnameseServicePage />} />
      <Route path="chuyen-doi-so" element={<VietnameseServicePage />} />
      <Route path="trung-tam-cuoc-goi" element={<VietnameseServicePage />} />
      <Route path="ho-tro-da-ngon-ngu" element={<VietnameseServicePage />} />
      {/* Swedish Service Landing Pages */}
      <Route path="callcenter-tjanster" element={<SwedishServicePage />} />
      <Route path="webbutveckling" element={<SwedishServicePage />} />
      <Route path="outsourcing-bpo-sv" element={<SwedishServicePage />} />
      <Route path="flersprakig-kundsupport" element={<SwedishServicePage />} />
      <Route path="digital-transformation-sv" element={<SwedishServicePage />} />
      {/* Romanian Service Landing Pages */}
      <Route path="servicii-call-center" element={<RomanianServicePage />} />
      <Route path="dezvoltare-web" element={<RomanianServicePage />} />
      <Route path="externalizare-bpo" element={<RomanianServicePage />} />
      <Route path="suport-clienti-multilingv" element={<RomanianServicePage />} />
      <Route path="transformare-digitala" element={<RomanianServicePage />} />
      {/* Czech Service Landing Pages */}
      <Route path="sluzby-call-centra" element={<CzechServicePage />} />
      <Route path="vyvoj-webu" element={<CzechServicePage />} />
      <Route path="outsourcing-bpo-cs" element={<CzechServicePage />} />
      <Route path="vicejazycna-zakaznicka-podpora" element={<CzechServicePage />} />
      <Route path="digitalni-transformace" element={<CzechServicePage />} />
      {/* Danish Service Landing Pages */}
      <Route path="callcenter-tjenester" element={<DanishServicePage />} />
      <Route path="webudvikling" element={<DanishServicePage />} />
      <Route path="outsourcing-bpo-da" element={<DanishServicePage />} />
      <Route path="flersproget-kundesupport" element={<DanishServicePage />} />
      <Route path="digital-transformation-da" element={<DanishServicePage />} />
      {/* Finnish Service Landing Pages */}
      <Route path="puhelinkeskuspalvelut" element={<FinnishServicePage />} />
      <Route path="verkkokehitys" element={<FinnishServicePage />} />
      <Route path="ulkoistus-bpo" element={<FinnishServicePage />} />
      <Route path="monikielinen-asiakastuki" element={<FinnishServicePage />} />
      <Route path="digitaalinen-muutos" element={<FinnishServicePage />} />
      {/* Greek Service Landing Pages */}
      <Route path="ypiresies-tilekentrikou" element={<GreekServicePage />} />
      <Route path="anaptyxi-istoselidon" element={<GreekServicePage />} />
      <Route path="exoteriki-anathesi-bpo" element={<GreekServicePage />} />
      <Route path="polyglossi-ypostirixi" element={<GreekServicePage />} />
      <Route path="psifiakos-metaschimatismos" element={<GreekServicePage />} />
      {/* Hungarian Service Landing Pages */}
      <Route path="call-center-szolgaltatasok" element={<HungarianServicePage />} />
      <Route path="webfejlesztes" element={<HungarianServicePage />} />
      <Route path="kiszervezes-bpo" element={<HungarianServicePage />} />
      <Route path="tobbnyelvű-ugyfelszolgalat" element={<HungarianServicePage />} />
      <Route path="digitalis-transzformacio" element={<HungarianServicePage />} />
      {/* SEO Dedicated Landing Pages */}
      <Route path="call-center" element={<CallCenterLandingPage />} />
      <Route path="web-development" element={<WebDevelopmentLandingPage />} />
      <Route path="app-development" element={<AppDevelopmentLandingPage />} />
      {/* Industry-specific landing pages */}
      <Route path="call-center-ecommerce" element={<IndustryLandingPage />} />
      <Route path="call-center-fintech" element={<IndustryLandingPage />} />
      <Route path="call-center-healthcare" element={<IndustryLandingPage />} />
      <Route path="call-center-saas" element={<IndustryLandingPage />} />
      <Route path="call-center-telecom" element={<IndustryLandingPage />} />
      <Route path="call-center-real-estate" element={<IndustryLandingPage />} />
      <Route path="call-center-travel" element={<IndustryLandingPage />} />
      <Route path="call-center-insurance" element={<IndustryLandingPage />} />
      <Route path="call-center-logistics" element={<IndustryLandingPage />} />
      <Route path="call-center-automotive" element={<IndustryLandingPage />} />
      <Route path="call-center-education" element={<IndustryLandingPage />} />
      <Route path="call-center-gaming" element={<IndustryLandingPage />} />
      <Route path="call-center-energy" element={<IndustryLandingPage />} />
      <Route path="call-center-government" element={<IndustryLandingPage />} />
      <Route path="call-center-media" element={<IndustryLandingPage />} />
      <Route path="call-center-pharma" element={<IndustryLandingPage />} />
      <Route path="call-center-banking" element={<IndustryLandingPage />} />
      <Route path="call-center-legal" element={<IndustryLandingPage />} />
      <Route path="call-center-retail" element={<IndustryLandingPage />} />
      <Route path="call-center-food-beverage" element={<IndustryLandingPage />} />
      <Route path="call-center-fashion" element={<IndustryLandingPage />} />
      <Route path="call-center-manufacturing" element={<IndustryLandingPage />} />
      <Route path="call-center-hospitality" element={<IndustryLandingPage />} />
      <Route path="call-center-airlines" element={<IndustryLandingPage />} />
      <Route path="call-center-hr-staffing" element={<IndustryLandingPage />} />
      <Route path="call-center-crypto" element={<IndustryLandingPage />} />
      <Route path="call-center-sports" element={<IndustryLandingPage />} />
      <Route path="call-center-construction" element={<IndustryLandingPage />} />
      {/* Technology stack landing pages */}
      <Route path="react-development" element={<IndustryLandingPage />} />
      <Route path="nodejs-development" element={<IndustryLandingPage />} />
      <Route path="python-development" element={<IndustryLandingPage />} />
      <Route path="angular-development" element={<IndustryLandingPage />} />
      <Route path="vuejs-development" element={<IndustryLandingPage />} />
      <Route path="php-development" element={<IndustryLandingPage />} />
      <Route path="java-development" element={<IndustryLandingPage />} />
      <Route path="dotnet-development" element={<IndustryLandingPage />} />
      <Route path="mobile-app-development" element={<IndustryLandingPage />} />
      <Route path="cloud-devops" element={<IndustryLandingPage />} />
      <Route path="ai-machine-learning" element={<IndustryLandingPage />} />
      <Route path="wordpress-development" element={<IndustryLandingPage />} />
      {/* Comparison/decision landing pages */}
      <Route path="why-outsource-call-center" element={<ComparisonLandingPage />} />
      <Route path="morocco-vs-india-call-center" element={<ComparisonLandingPage />} />
      <Route path="morocco-vs-philippines-call-center" element={<ComparisonLandingPage />} />
      <Route path="morocco-vs-tunisia-call-center" element={<ComparisonLandingPage />} />
      <Route path="nearshore-vs-offshore-bpo" element={<ComparisonLandingPage />} />
      <Route path="call-center-pricing" element={<ComparisonLandingPage />} />
      <Route path="call-center-roi-calculator" element={<ComparisonLandingPage />} />
      <Route path="ai-call-center-2026" element={<ComparisonLandingPage />} />
      <Route path="saas-support-outsourcing" element={<ComparisonLandingPage />} />
      {/* Additional comparison/landing pages referenced in sitemap */}
      <Route path="expertise" element={<LangRedirect to="/services" />} />
      <Route path="inhouse-vs-outsourced-call-center" element={<ComparisonLandingPage />} />
      <Route path="shared-vs-dedicated-agents" element={<ComparisonLandingPage />} />
      <Route path="onshore-vs-nearshore-vs-offshore" element={<ComparisonLandingPage />} />
      <Route path="bpo-vs-kpo" element={<ComparisonLandingPage />} />
      <Route path="call-center-nonprofit" element={<IndustryLandingPage />} />
      {/* Industry × Country cross-pages (1320 combinations: 22 industries × 60 countries) */}
      {['ecommerce','fintech','healthcare','saas','telecom','insurance','real-estate','travel','logistics','automotive','education','gaming','energy','government','media','pharma','nonprofit','banking','legal','retail','food-beverage','fashion','manufacturing','hospitality','airlines','hr-staffing','crypto','sports','construction'].flatMap(ind =>
        ['france','germany','uk','usa','uae','spain','netherlands','canada','switzerland','italy','morocco','belgium','saudi-arabia','qatar','portugal','japan','korea','turkey','brazil','india','australia','south-africa','mexico','colombia','poland','philippines','nigeria','egypt','sweden','denmark','romania','singapore','indonesia','argentina','thailand','malaysia','vietnam','greece','chile','peru','austria','ireland','czech-republic','finland','hungary','bahrain','kuwait','oman','jordan','lebanon','bangladesh','pakistan','sri-lanka','myanmar','cambodia','kenya','ghana','senegal','tunisia','ivory-coast'].map(country =>
          <Route key={`${ind}-${country}`} path={`${ind}-bpo-${country}`} element={<IndustryCountryLandingPage />} />
        )
      )}
      <Route path="blog" element={<BlogPage />} />
      <Route path="blog/:slug" element={<BlogArticlePage />} />
      
      {/* Required Service Routes (exact paths from requirements) */}
      <Route path="services/customer-support" element={<CustomerSupportPage />} />
      <Route path="services/bpo/customer-support" element={<CustomerSupportPage />} />
      <Route path="services/tele-sales" element={<TelesalesPage />} />
      <Route path="services/back-office" element={<BackofficePage />} />
      <Route path="services/content-moderation" element={<ContentModerationPage />} />
      <Route path="services/web-development" element={<WebDevelopmentPage />} />
      <Route path="services/mobile-apps" element={<MobileAppsServicePage />} />
      <Route path="services/ai-solutions" element={<AISolutionsPage />} />
      
      {/* BPO Service Detail Pages - Must be BEFORE dynamic route */}
      <Route path="services/bpo/inbound-customer-care" element={<InboundCustomerCarePage />} />
      <Route path="services/bpo/technical-support" element={<TechnicalSupportPage />} />
      <Route path="services/bpo/outbound-sales" element={<OutboundSalesPage />} />
      <Route path="services/bpo/retention-winback" element={<RetentionWinbackPage />} />
      <Route path="services/bpo/sdr-appointment-setting" element={<SDRAppointmentSettingPage />} />
      <Route path="services/bpo/market-research-surveys" element={<MarketResearchPage />} />
      <Route path="services/bpo/digital-engagement" element={<DigitalEngagementPage />} />
      <Route path="services/bpo/trust-safety-compliance" element={<TrustSafetyPage />} />
      <Route path="services/bpo/ai-ops-backoffice" element={<AIOpsBackofficePage />} />
      <Route path="services/bpo/multilingual-support" element={<MultilingualSupportPage />} />
      <Route path="services/bpo/order-management" element={<OrderManagementPage />} />
      <Route path="services/bpo/billing-collections" element={<BillingCollectionsPage />} />
      <Route path="services/bpo/customer-retention" element={<CustomerRetentionPage />} />
      <Route path="services/bpo/lead-qualification" element={<LeadQualificationPage />} />
      <Route path="services/bpo/live-chat-messaging" element={<LiveChatMessagingPage />} />
      <Route path="services/bpo/social-media-care" element={<SocialMediaCarePage />} />
      <Route path="services/bpo/customer-success-saas" element={<CustomerSuccessSaaSPage />} />
      <Route path="services/bpo/collections-payment" element={<CollectionsPaymentPage />} />
      <Route path="services/bpo/qa-coaching" element={<QACoachingPage />} />
      {/* Expansion BPO Service Pages */}
      <Route path="services/bpo/finance-accounting" element={<FinanceAccountingPage />} />
      <Route path="services/bpo/hr-payroll" element={<HRPayrollPage />} />
      <Route path="services/bpo/healthcare-bpo" element={<HealthcareBPOPage />} />
      <Route path="services/bpo/logistics-supply-chain" element={<LogisticsSupplyChainPage />} />
      <Route path="services/bpo/virtual-assistant" element={<VirtualAssistantPage />} />
      <Route path="services/bpo/recruitment-staffing" element={<RecruitmentStaffingPage />} />
      <Route path="services/bpo/fraud-detection" element={<FraudDetectionPage />} />
      <Route path="services/bpo/compliance-monitoring" element={<ComplianceMonitoringPage />} />
      <Route path="services/bpo/content-review" element={<ContentReviewPage />} />
      <Route path="services/bpo/data-entry-processing" element={<DataEntryProcessingPage />} />
      
      {/* Digital Studio Service Detail Pages - Must be BEFORE dynamic route */}
      <Route path="services/digital-studio/quality-engineering" element={<QualityEngineeringPage />} />
      <Route path="services/digital-studio/custom-saas" element={<CustomSaaSPage />} />
      <Route path="services/digital-studio/enterprise-portals" element={<EnterprisePortalsPage />} />
      <Route path="services/digital-studio/ecommerce" element={<EcommercePage />} />
      <Route path="services/digital-studio/api-integration" element={<APIIntegrationPage />} />
      <Route path="services/digital-studio/mobile-apps" element={<MobileAppsPage />} />
      <Route path="services/digital-studio/ui-ux-design" element={<UIUXDesignPage />} />
      <Route path="services/digital-studio/cloud-devops" element={<CloudDevOpsPage />} />
      <Route path="services/digital-studio/growth-marketing" element={<GrowthMarketingPage />} />
      <Route path="services/digital-studio/emerging-tech-ai" element={<EmergingTechAIPage />} />
      <Route path="services/digital-studio/tech-stack-showcase" element={<TechStackShowcasePage />} />
      <Route path="services/digital-studio/synergy" element={<SynergyPage />} />
      <Route path="services/digital-studio/data-engineering-bi" element={<DataEngineeringBIPage />} />
      <Route path="services/digital-studio/cybersecurity-appsec" element={<CybersecurityAppSecPage />} />
      <Route path="services/digital-studio/managed-services" element={<ManagedServicesPage />} />
      <Route path="services/digital-studio/devsecops" element={<DevSecOpsPage />} />
      <Route path="services/digital-studio/reliability-observability" element={<ReliabilityObservabilityPage />} />
      {/* Expansion Digital Studio Pages */}
      <Route path="services/digital-studio/ai-ml-development" element={<AIMLDevelopmentPage />} />
      <Route path="services/digital-studio/blockchain-web3" element={<BlockchainWeb3Page />} />
      <Route path="services/digital-studio/iot-development" element={<IoTDevelopmentPage />} />
      <Route path="services/digital-studio/legacy-modernization" element={<LegacyModernizationPage />} />
      <Route path="services/digital-studio/staff-augmentation" element={<StaffAugmentationPage />} />
      <Route path="services/digital-studio/ar-vr-development" element={<ARVRDevelopmentPage />} />
      <Route path="services/digital-studio/digital-transformation" element={<DigitalTransformationDigitalPage />} />
      <Route path="services/digital-studio/data-science-analytics" element={<DataScienceAnalyticsPage />} />
      <Route path="services/digital-studio/qa-testing" element={<QATestingPage />} />
      <Route path="services/digital-studio/devops-cloud-migration" element={<DevOpsCloudMigrationPage />} />
      <Route path="services/digital-studio/devops-automation" element={<DevOpsCloudMigrationPage />} />
      <Route path="services/digital-studio/penetration-testing" element={<PenetrationTestingPage />} />
      <Route path="services/digital-studio/soc-as-a-service" element={<SOCAsAServicePage />} />
      <Route path="services/digital-studio/mobile-development" element={<MobileDevelopmentPage />} />
      <Route path="services/digital-studio/web-development" element={<WebDevelopmentDigitalPage />} />
      
      {/* Part 4: New Service Routes (Template-based BPO, Digital, Consulting) */}
      {/* BPO Services */}
      <Route path="services/bpo/inbound-customer-support" element={<InboundCustomerSupportPage />} />
      {/* outbound-sales handled above at line 723 */}
      {/* technical-support route handled at line 658 by TechnicalSupportPage */}
      <Route path="services/bpo/back-office-data-processing" element={<BackOfficeDataProcessingPage />} />
      <Route path="services/bpo/chat-email-support" element={<ChatEmailSupportPage />} />
      <Route path="services/bpo/social-media-management" element={<SocialMediaManagementPage />} />
      <Route path="services/bpo/content-moderation" element={<ContentModerationPageNew />} />
      <Route path="services/bpo/whatsapp-business-support" element={<WhatsAppBusinessSupportPage />} />
      <Route path="services/bpo/phone-support" element={<PhoneSupportPage />} />
      <Route path="services/bpo/24-7-support" element={<TwentyFourSevenSupportPage />} />
      <Route path="services/bpo/email-support" element={<EmailSupportPage />} />
      
      {/* Direct service route aliases (Part A shortcut paths) */}
      <Route path="services/live-chat-support" element={<ChatEmailSupportPage />} />
      <Route path="services/order-management" element={<OrderManagementPage />} />
      <Route path="services/billing-collections" element={<BillingCollectionsPage />} />
      <Route path="services/healthcare-bpo" element={<HealthcareBPOPage />} />
      <Route path="services/finance-accounting-bpo" element={<FinanceAccountingPage />} />
      <Route path="services/hr-payroll-outsourcing" element={<HRPayrollPage />} />
      <Route path="services/market-research" element={<MarketResearchPage />} />
      <Route path="services/quality-assurance" element={<QACoachingPage />} />
      <Route path="services/workforce-management" element={<WorkforceManagementPage />} />

      {/* Comparison/Calculator pages (Part C) */}
      <Route path="outsourcing-cost-calculator" element={<ComparisonLandingPage />} />
      <Route path="bpo-pricing-guide-2026" element={<ComparisonLandingPage />} />
      <Route path="morocco-vs-india-outsourcing" element={<ComparisonLandingPage />} />
      <Route path="in-house-vs-outsourced-call-center" element={<ComparisonLandingPage />} />
      <Route path="nearshore-vs-offshore-bpo-2026" element={<ComparisonLandingPage />} />

      {/* City Agency Pages */}
      {/* AGENCY-ROUTES-1910: sitemap slugs with existing CITY_AGENCY_PAGES data that had no route */}
      <Route path="software-development-agency-almaty" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-antananarivo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-baghdad" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bamako" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-brazzaville" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-caracas" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-conakry" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cotonou" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-daegu" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-doha-enterprise" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-essen" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-freetown" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-gaborone" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-guatemala-city" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hanover" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-harare" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-havana" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kathmandu" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-khartoum" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kingston" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kinshasa" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kosice" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kumasi" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-la-paz" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-libreville" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-managua" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-manama" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-monrovia" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-ndjamena" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-niamey" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-ouagadougou" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-phnom-penh" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-podgorica" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-port-moresby" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-port-of-spain" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-pune-enterprise" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-san-jose-costa-rica" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-san-salvador" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-santo-domingo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-surat" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-suva" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tegucigalpa" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tehran" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tripoli" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-ulaanbaatar" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-vientiane" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-yaounde" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-casablanca" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-paris" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-london" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-berlin" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-madrid" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dubai" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lisbon" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-amsterdam" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-warsaw" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-istanbul" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-stockholm" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-milan" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sao-paulo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-mumbai" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tokyo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-copenhagen" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-oslo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-helsinki" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dublin" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-zurich" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-singapore" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-seoul" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sydney" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-toronto" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-mexico-city" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-buenos-aires" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lagos" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bangkok" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kuala-lumpur" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-athens" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-vienna" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-prague" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bucharest" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cairo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-riyadh" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-johannesburg" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nairobi" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-manila" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-ho-chi-minh" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bogota" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lima" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-doha" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-muscat" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-accra" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tunis" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-beirut" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-amman" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dar-es-salaam" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kigali" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-abidjan" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dakar" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bangalore" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-jakarta" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-santiago" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-monterrey" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-addis-ababa" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lusaka" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hanoi" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dhaka" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-karachi" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-medellin" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-quito" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-panama-city" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tbilisi" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tallinn" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bratislava" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-budapest" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-zagreb" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-riga" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-vilnius" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cape-town" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kampala" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-maputo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-algiers" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-colombo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kyiv" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tashkent" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-baku" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-yerevan" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-skopje" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sofia" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-belgrade" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-montevideo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-san-jose-cr" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-port-louis" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-minsk" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-astana" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bishkek" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-chisinau" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sarajevo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tirana" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nicosia" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-valletta" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-luxembourg" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-reykjavik" element={<CityAgencyLandingPage />} />
      {/* Wave 10: China + Global */}
      <Route path="software-development-agency-shanghai" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-beijing" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-shenzhen" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-guangzhou" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-chengdu" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hong-kong" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-taipei" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hyderabad" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tel-aviv" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-denver" element={<CityAgencyLandingPage />} />
      {/* Wave 11: China expanded + Global */}
      <Route path="software-development-agency-wuhan" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hangzhou" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nanjing" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-penang" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lahore" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-jeddah" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-durban" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-auckland" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-vancouver" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-chicago" element={<CityAgencyLandingPage />} />
      {/* Wave 12: China deep + Global */}
      <Route path="software-development-agency-xian" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dalian" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-suzhou" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-pune" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cebu" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-medina" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-port-harcourt" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-curitiba" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-krakow" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-marseille" element={<CityAgencyLandingPage />} />
      {/* Wave 13: China/India/Pakistan/Mexico/France/Germany/UK + Africa */}
      <Route path="software-development-agency-tianjin" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-qingdao" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-chennai" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-islamabad" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-rabat" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-abuja" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-guadalajara" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lyon" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-munich" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-edinburgh" element={<CityAgencyLandingPage />} />
      {/* Wave 14: China/India/Morocco/Germany/Spain/UK/Kuwait */}
      <Route path="software-development-agency-changsha" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-xiamen" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kolkata" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-fez" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hamburg" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-barcelona" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kuwait-city" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-manchester" element={<CityAgencyLandingPage />} />
      {/* Wave 15: Americas Hub + Japan/Brazil/Africa/Korea/Argentina/Oman/Portugal */}
      <Route path="software-development-agency-miami-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-osaka" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-recife" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-praia" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-busan" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-mombasa" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-rosario" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-muscat-tech" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-porto" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-fukuoka" element={<CityAgencyLandingPage />} />
      {/* Wave 16: Turkey/Belgium Hub/Morocco Hub/Sweden/Netherlands/Italy/Argentina/Morocco/Japan/India */}
      <Route path="software-development-agency-ankara" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-brussels-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-casablanca-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-gothenburg" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-rotterdam" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-florence" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cordoba" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tangier" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nagoya" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-ahmedabad" element={<CityAgencyLandingPage />} />
      {/* Wave 17: Bratislava Hub/Tallinn Hub/Lisbon Hub/Seville/Antwerp/Stuttgart/Osaka Hub/Shenzhen Hub/Cape Town Hub/Lagos Hub */}
      <Route path="software-development-agency-bratislava-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tallinn-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lisbon-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-seville" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-antwerp" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-stuttgart" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-osaka-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-shenzhen-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cape-town-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lagos-hub" element={<CityAgencyLandingPage />} />
      {/* Wave 18: Malmö/Eindhoven/Turin/Kraków Hub/Valencia/Thessaloniki/Izmir/Chongqing/Pune Hub/Nairobi Hub */}
      <Route path="software-development-agency-malmo" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-eindhoven" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-turin" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-krakow-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-valencia" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-thessaloniki" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-izmir" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-chongqing" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-pune-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nairobi-hub" element={<CityAgencyLandingPage />} />
      {/* Wave 19: Bilbao/Gdańsk/Brno/Cluj-Napoca/Porto Hub/Marrakech/Coimbatore/Fukuoka Hub/Accra Hub/Monterrey Hub */}
      <Route path="software-development-agency-bilbao" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-gdansk" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-brno" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cluj-napoca" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-porto-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-marrakech" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-coimbatore" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-fukuoka-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-accra-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-monterrey-hub" element={<CityAgencyLandingPage />} />
      {/* Wave 20: Nantes/Düsseldorf/Wrocław/Timișoara/Limassol/Jaipur/Busan Hub/Medellín Hub/Windhoek/Bali */}
      <Route path="software-development-agency-nantes" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dusseldorf" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-wroclaw" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-timisoara" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-limassol" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-jaipur" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-busan-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-medellin-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-windhoek" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bali" element={<CityAgencyLandingPage />} />
      {/* Wave 21: Bordeaux/Frankfurt/Poznań/Iași/Tallinn Tech/Lucknow/Taipei Hub/Quito Hub/Luanda/Colombo Hub */}
      <Route path="software-development-agency-bordeaux" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-frankfurt" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-poznan" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-iasi" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tallinn-tech" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lucknow" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-taipei-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-quito-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-luanda" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-colombo-hub" element={<CityAgencyLandingPage />} />
      {/* Wave 22: Toulouse/Cologne/Łódź/Sibiu/Thessaloniki Hub/Chandigarh/HCM Hub/São Paulo Hub/Dakar Hub/Muscat Hub */}
      <Route path="software-development-agency-toulouse" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-cologne" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lodz" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sibiu" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-thessaloniki-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-chandigarh" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hcm-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sao-paulo-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dakar-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-muscat-hub2" element={<CityAgencyLandingPage />} />
      {/* Wave 23+: Unique hub/expansion routes only (duplicates removed) */}
      <Route path="software-development-agency-strasbourg" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-katowice" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-riga-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-osaka-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-montevideo-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-abidjan-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-manama-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-leipzig" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sofia-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-vilnius-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-jakarta-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-santiago-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kigali-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kuwait-city-hub" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lille" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nuremberg" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-szczecin" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-constanta" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-tartu" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-indore" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-nagoya-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-asuncion" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-lome" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-jeddah-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-dortmund" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-douala" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bahrain-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hannover" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-ljubljana" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-hyderabad-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-bogota-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-kaunas" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-quito-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-sharjah" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-rennes" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-accra-hub2" element={<CityAgencyLandingPage />} />
      <Route path="software-development-agency-manama-hub3" element={<CityAgencyLandingPage />} />

      {/* Short city slugs (canonical in sitemap-cities-*.xml) */}
      <Route path="ahmedabad" element={<CityAgencyLandingPage />} />
      <Route path="bratislava" element={<CityAgencyLandingPage />} />
      <Route path="daegu" element={<CityAgencyLandingPage />} />
      <Route path="helsinki" element={<CityAgencyLandingPage />} />
      <Route path="kumasi" element={<CityAgencyLandingPage />} />
      <Route path="kuwait-city" element={<CityAgencyLandingPage />} />
      <Route path="nuremberg" element={<CityAgencyLandingPage />} />
      <Route path="reykjavik" element={<CityAgencyLandingPage />} />
      <Route path="san-jose-costa-rica" element={<CityAgencyLandingPage />} />
      <Route path="strasbourg" element={<CityAgencyLandingPage />} />
      {ALL_CITY_ROUTE_SLUGS
        .filter(
          (slug) =>
            !slug.startsWith('software-development-agency-') &&
            ![
              'ahmedabad',
              'bratislava',
              'daegu',
              'helsinki',
              'kumasi',
              'kuwait-city',
              'nuremberg',
              'reykjavik',
              'san-jose-costa-rica',
              'strasbourg'
            ].includes(slug)
        )
        .map((slug) => (
          <Route key={`city-short-${slug}`} path={slug} element={<CityAgencyLandingPage />} />
        ))}

      {/* City Service Pages — call-center-{city} and web-development-{city} */}
      <Route path="call-center-lagos" element={<CityServiceLandingPage />} />
      <Route path="call-center-nairobi" element={<CityServiceLandingPage />} />
      <Route path="call-center-accra" element={<CityServiceLandingPage />} />
      <Route path="call-center-addis-ababa" element={<CityServiceLandingPage />} />
      <Route path="call-center-dar-es-salaam" element={<CityServiceLandingPage />} />
      <Route path="call-center-kigali" element={<CityServiceLandingPage />} />
      <Route path="call-center-kampala" element={<CityServiceLandingPage />} />
      <Route path="call-center-lusaka" element={<CityServiceLandingPage />} />
      <Route path="call-center-maputo" element={<CityServiceLandingPage />} />
      <Route path="call-center-abidjan" element={<CityServiceLandingPage />} />
      <Route path="call-center-dakar" element={<CityServiceLandingPage />} />
      <Route path="call-center-tunis" element={<CityServiceLandingPage />} />
      <Route path="call-center-algiers" element={<CityServiceLandingPage />} />
      <Route path="call-center-rabat" element={<CityServiceLandingPage />} />
      <Route path="call-center-tripoli" element={<CityServiceLandingPage />} />
      <Route path="call-center-luanda" element={<CityServiceLandingPage />} />
      <Route path="call-center-douala" element={<CityServiceLandingPage />} />
      <Route path="call-center-kinshasa" element={<CityServiceLandingPage />} />
      <Route path="call-center-harare" element={<CityServiceLandingPage />} />
      <Route path="call-center-windhoek" element={<CityServiceLandingPage />} />
      <Route path="call-center-gaborone" element={<CityServiceLandingPage />} />
      <Route path="call-center-muscat" element={<CityServiceLandingPage />} />
      <Route path="call-center-doha" element={<CityServiceLandingPage />} />
      <Route path="call-center-kuwait-city" element={<CityServiceLandingPage />} />
      <Route path="call-center-manama" element={<CityServiceLandingPage />} />
      <Route path="call-center-amman" element={<CityServiceLandingPage />} />
      <Route path="call-center-beirut" element={<CityServiceLandingPage />} />
      <Route path="call-center-tbilisi" element={<CityServiceLandingPage />} />
      <Route path="call-center-baku" element={<CityServiceLandingPage />} />
      <Route path="call-center-yerevan" element={<CityServiceLandingPage />} />
      <Route path="call-center-tashkent" element={<CityServiceLandingPage />} />
      <Route path="call-center-almaty" element={<CityServiceLandingPage />} />
      <Route path="call-center-bishkek" element={<CityServiceLandingPage />} />
      <Route path="call-center-dhaka" element={<CityServiceLandingPage />} />
      <Route path="call-center-colombo" element={<CityServiceLandingPage />} />
      <Route path="call-center-kathmandu" element={<CityServiceLandingPage />} />
      <Route path="call-center-yangon" element={<CityServiceLandingPage />} />
      <Route path="call-center-phnom-penh" element={<CityServiceLandingPage />} />
      <Route path="call-center-vientiane" element={<CityServiceLandingPage />} />
      <Route path="call-center-ulaanbaatar" element={<CityServiceLandingPage />} />
      <Route path="call-center-suva" element={<CityServiceLandingPage />} />
      <Route path="call-center-port-moresby" element={<CityServiceLandingPage />} />
      <Route path="call-center-auckland" element={<CityServiceLandingPage />} />
      <Route path="call-center-wellington" element={<CityServiceLandingPage />} />
      <Route path="call-center-perth" element={<CityServiceLandingPage />} />
      <Route path="call-center-adelaide" element={<CityServiceLandingPage />} />
      <Route path="call-center-brisbane" element={<CityServiceLandingPage />} />
      <Route path="call-center-gold-coast" element={<CityServiceLandingPage />} />
      <Route path="call-center-christchurch" element={<CityServiceLandingPage />} />
      <Route path="call-center-hamilton-nz" element={<CityServiceLandingPage />} />
      <Route path="call-center-canberra" element={<CityServiceLandingPage />} />
      {/* Batch 2 — Secondary BPO cities */}
      <Route path="call-center-edinburgh" element={<CityServiceLandingPage />} />
      <Route path="call-center-manchester" element={<CityServiceLandingPage />} />
      <Route path="call-center-munich" element={<CityServiceLandingPage />} />
      <Route path="call-center-hamburg" element={<CityServiceLandingPage />} />
      <Route path="call-center-lyon" element={<CityServiceLandingPage />} />
      <Route path="call-center-krakow" element={<CityServiceLandingPage />} />
      <Route path="call-center-sofia" element={<CityServiceLandingPage />} />
      <Route path="call-center-belgrade" element={<CityServiceLandingPage />} />
      <Route path="call-center-tallinn" element={<CityServiceLandingPage />} />
      <Route path="call-center-riga" element={<CityServiceLandingPage />} />
      <Route path="call-center-sharjah" element={<CityServiceLandingPage />} />
      <Route path="call-center-tel-aviv" element={<CityServiceLandingPage />} />
      <Route path="call-center-marrakech" element={<CityServiceLandingPage />} />
      <Route path="call-center-tangier" element={<CityServiceLandingPage />} />
      <Route path="call-center-fez" element={<CityServiceLandingPage />} />
      <Route path="call-center-durban" element={<CityServiceLandingPage />} />
      <Route path="call-center-port-harcourt" element={<CityServiceLandingPage />} />
      <Route path="call-center-cebu" element={<CityServiceLandingPage />} />
      <Route path="call-center-islamabad" element={<CityServiceLandingPage />} />
      <Route path="call-center-medellin" element={<CityServiceLandingPage />} />
      <Route path="call-center-guadalajara" element={<CityServiceLandingPage />} />
      <Route path="call-center-montevideo" element={<CityServiceLandingPage />} />
      {/* Batch 3 — Emerging & tertiary BPO cities */}
      <Route path="call-center-vilnius" element={<CityServiceLandingPage />} />
      <Route path="call-center-zagreb" element={<CityServiceLandingPage />} />
      <Route path="call-center-ljubljana" element={<CityServiceLandingPage />} />
      <Route path="call-center-tirana" element={<CityServiceLandingPage />} />
      <Route path="call-center-skopje" element={<CityServiceLandingPage />} />
      <Route path="call-center-porto" element={<CityServiceLandingPage />} />
      <Route path="call-center-malmo" element={<CityServiceLandingPage />} />
      <Route path="call-center-gdansk" element={<CityServiceLandingPage />} />
      <Route path="call-center-wroclaw" element={<CityServiceLandingPage />} />
      <Route path="call-center-cluj-napoca" element={<CityServiceLandingPage />} />
      <Route path="call-center-timisoara" element={<CityServiceLandingPage />} />
      <Route path="call-center-nicosia" element={<CityServiceLandingPage />} />
      <Route path="call-center-yaounde" element={<CityServiceLandingPage />} />
      <Route path="call-center-lome" element={<CityServiceLandingPage />} />
      <Route path="call-center-ouagadougou" element={<CityServiceLandingPage />} />
      <Route path="call-center-bamako" element={<CityServiceLandingPage />} />
      <Route path="call-center-conakry" element={<CityServiceLandingPage />} />
      <Route path="call-center-freetown" element={<CityServiceLandingPage />} />
      <Route path="call-center-niamey" element={<CityServiceLandingPage />} />
      <Route path="call-center-libreville" element={<CityServiceLandingPage />} />
      <Route path="call-center-brazzaville" element={<CityServiceLandingPage />} />
      <Route path="call-center-port-louis" element={<CityServiceLandingPage />} />
      <Route path="call-center-ankara" element={<CityServiceLandingPage />} />
      <Route path="call-center-izmir" element={<CityServiceLandingPage />} />
      <Route path="call-center-lahore" element={<CityServiceLandingPage />} />
      <Route path="call-center-jaipur" element={<CityServiceLandingPage />} />
      <Route path="call-center-surat" element={<CityServiceLandingPage />} />
      <Route path="call-center-lucknow" element={<CityServiceLandingPage />} />
      <Route path="call-center-indore" element={<CityServiceLandingPage />} />
      <Route path="call-center-busan" element={<CityServiceLandingPage />} />
      <Route path="call-center-nagoya" element={<CityServiceLandingPage />} />
      <Route path="call-center-penang" element={<CityServiceLandingPage />} />
      <Route path="call-center-santo-domingo" element={<CityServiceLandingPage />} />
      <Route path="call-center-san-salvador" element={<CityServiceLandingPage />} />
      <Route path="call-center-tegucigalpa" element={<CityServiceLandingPage />} />
      <Route path="call-center-cordoba" element={<CityServiceLandingPage />} />
      <Route path="web-development-lagos" element={<CityServiceLandingPage />} />
      <Route path="web-development-nairobi" element={<CityServiceLandingPage />} />
      <Route path="web-development-accra" element={<CityServiceLandingPage />} />
      <Route path="web-development-addis-ababa" element={<CityServiceLandingPage />} />
      <Route path="web-development-dar-es-salaam" element={<CityServiceLandingPage />} />
      <Route path="web-development-kigali" element={<CityServiceLandingPage />} />
      <Route path="web-development-kampala" element={<CityServiceLandingPage />} />
      <Route path="web-development-lusaka" element={<CityServiceLandingPage />} />
      <Route path="web-development-maputo" element={<CityServiceLandingPage />} />
      <Route path="web-development-abidjan" element={<CityServiceLandingPage />} />
      <Route path="web-development-dakar" element={<CityServiceLandingPage />} />
      <Route path="web-development-tunis" element={<CityServiceLandingPage />} />
      <Route path="web-development-algiers" element={<CityServiceLandingPage />} />
      <Route path="web-development-tripoli" element={<CityServiceLandingPage />} />
      <Route path="web-development-luanda" element={<CityServiceLandingPage />} />
      <Route path="web-development-douala" element={<CityServiceLandingPage />} />
      <Route path="web-development-kinshasa" element={<CityServiceLandingPage />} />
      <Route path="web-development-harare" element={<CityServiceLandingPage />} />
      <Route path="web-development-windhoek" element={<CityServiceLandingPage />} />
      <Route path="web-development-gaborone" element={<CityServiceLandingPage />} />
      <Route path="web-development-muscat" element={<CityServiceLandingPage />} />
      <Route path="web-development-doha" element={<CityServiceLandingPage />} />
      <Route path="web-development-kuwait-city" element={<CityServiceLandingPage />} />
      <Route path="web-development-manama" element={<CityServiceLandingPage />} />
      <Route path="web-development-amman" element={<CityServiceLandingPage />} />
      <Route path="web-development-beirut" element={<CityServiceLandingPage />} />
      <Route path="web-development-tbilisi" element={<CityServiceLandingPage />} />
      <Route path="web-development-baku" element={<CityServiceLandingPage />} />
      <Route path="web-development-yerevan" element={<CityServiceLandingPage />} />
      <Route path="web-development-tashkent" element={<CityServiceLandingPage />} />
      <Route path="web-development-almaty" element={<CityServiceLandingPage />} />
      <Route path="web-development-bishkek" element={<CityServiceLandingPage />} />
      <Route path="web-development-dhaka" element={<CityServiceLandingPage />} />
      <Route path="web-development-colombo" element={<CityServiceLandingPage />} />
      <Route path="web-development-kathmandu" element={<CityServiceLandingPage />} />
      <Route path="web-development-yangon" element={<CityServiceLandingPage />} />
      <Route path="web-development-phnom-penh" element={<CityServiceLandingPage />} />
      <Route path="web-development-vientiane" element={<CityServiceLandingPage />} />
      <Route path="web-development-ulaanbaatar" element={<CityServiceLandingPage />} />
      <Route path="web-development-suva" element={<CityServiceLandingPage />} />
      <Route path="web-development-port-moresby" element={<CityServiceLandingPage />} />
      <Route path="web-development-auckland" element={<CityServiceLandingPage />} />
      <Route path="web-development-wellington" element={<CityServiceLandingPage />} />
      <Route path="web-development-perth" element={<CityServiceLandingPage />} />
      <Route path="web-development-adelaide" element={<CityServiceLandingPage />} />
      <Route path="web-development-brisbane" element={<CityServiceLandingPage />} />
      <Route path="web-development-gold-coast" element={<CityServiceLandingPage />} />
      <Route path="web-development-christchurch" element={<CityServiceLandingPage />} />
      <Route path="web-development-hamilton-nz" element={<CityServiceLandingPage />} />
      <Route path="web-development-canberra" element={<CityServiceLandingPage />} />

      {/* Software Development City Pages — software-development-{city} */}
      {SOFT_DEV_CITY_KEYS.map(slug => (
        <Route key={`sd-city-${slug}`} path={`software-development-${slug}`} element={<SoftwareDevCityPage />} />
      ))}

      {/* Software Development Region Pages — software-development-{region} */}
      {SOFT_DEV_REGION_KEYS.map(slug => (
        <Route key={`sd-region-${slug}`} path={`software-development-${slug}`} element={<SoftwareDevRegionPage />} />
      ))}

      {/* Continent Hub Pages */}
      <Route path="outsourcing/:continentSlug" element={<ContinentHubPage />} />
      
      {/* Service Directory */}
      <Route path="services/directory" element={<ServiceDirectoryPage />} />
      
      {/* Webinars & Academy */}
      <Route path="webinars" element={<WebinarsPage />} />
      <Route path="academy" element={<AcademyPage />} />

      {/* Legacy /services/digital/* → canonical /services/digital-studio/* redirects */}
      <Route path="services/digital/web-development" element={<LangRedirect to="/services/digital-studio/web-development" />} />
      <Route path="services/digital/mobile-development" element={<LangRedirect to="/services/digital-studio/mobile-apps" />} />
      <Route path="services/digital/mobile-app-development" element={<LangRedirect to="/services/digital-studio/mobile-apps" />} />
      <Route path="services/digital/saas-development" element={<LangRedirect to="/services/digital-studio/custom-saas" />} />
      <Route path="services/digital/api-integration" element={<LangRedirect to="/services/digital-studio/api-integration" />} />
      <Route path="services/digital/ui-ux-design" element={<LangRedirect to="/services/digital-studio/ui-ux-design" />} />
      <Route path="services/digital/cloud-devops" element={<LangRedirect to="/services/digital-studio/cloud-devops" />} />
      <Route path="services/digital/ai-automation" element={<LangRedirect to="/services/digital-studio/emerging-tech-ai" />} />
      
      {/* Consulting Services */}
      <Route path="services/consulting/cx-strategy" element={<CXStrategyAuditPage />} />
      <Route path="services/consulting/digital-transformation" element={<DigitalTransformationPage />} />
      
      {/* Procurement BPO — canonical cluster page (must precede dynamic :category/:subcategory) */}
      <Route path="services/procurement-bpo-outsourcing" element={<ProcurementBPOPage />} />
      {/* PROCUREMENT-1910: alias without /services served a soft 404 -> canonical service page */}
      <Route path="procurement-bpo-outsourcing" element={<LangRedirectKeepQuery to="/services/procurement-bpo-outsourcing" />} />
      {/* Dynamic Service Routes - Fallback for any other services */}
      <Route path="services/:category/:subcategory" element={<ServiceConfiguratorPage />} />
      {/* Solutions Pages */}
      <Route path="solutions" element={<SolutionsHubPage />} />
      <Route path="solutions/customer-experience-augmented" element={<CustomerExperiencePage />} />
      <Route path="solutions/bpo-contact-center" element={<BPOContactCenterPage />} />
      <Route path="solutions/digital-factory" element={<DigitalFactoryPage />} />
      <Route path="solutions/trust-and-safety" element={<TrustAndSafetyPage />} />
      <Route path="solutions/best-in-class-apps" element={<BestInClassAppsPage />} />
      <Route path="solutions/reference-architecture" element={<ReferenceArchitecturePage />} />
      <Route path="solutions/routing-playbook" element={<RoutingPlaybookPage />} />
      <Route path="solutions/startup-growth-pack" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/enterprise-digital-transformation" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/ecommerce-success-bundle" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/saas-scale-package" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/healthcare-compliance-suite" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/fintech-operations-bundle" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/multilingual-expansion-pack" element={<ServiceBundleLandingPage />} />
      <Route path="solutions/ai-powered-support-suite" element={<ServiceBundleLandingPage />} />
      
      {/* Process Pages */}
      <Route path="process/onboarding" element={<ProcessLandingPage />} />
      <Route path="process/quality-assurance" element={<ProcessLandingPage />} />
      <Route path="process/recruitment" element={<ProcessLandingPage />} />
      <Route path="process/technology-stack" element={<ProcessLandingPage />} />
      <Route path="process/security-compliance" element={<ProcessLandingPage />} />
      
      {/* Industries - dynamic sub-routes only (main /industries handled above) */}
      {/* Industries SEO Landing Pages — 15 verticals (must be before :industry catch-all) */}
      {['healthcare-bpo','financial-services-outsourcing','telecom-bpo','insurance-outsourcing','real-estate-virtual-assistants','legal-process-outsourcing','education-edtech-services','automotive-bpo','energy-utilities-support','government-public-sector','retail-customer-service','logistics-supply-chain','media-entertainment-support','banking-financial-services','retail-consumer-goods','manufacturing-industrial','hospitality-hotels','aviation-airlines'].map(slug => (
        <Route key={`ind-${slug}`} path={`industries/${slug}`} element={<IndustrySEOPage />} />
      ))}
      <Route path="industries/fintech" element={<LangRedirectKeepQuery to="/industries/finance" />} />
      <Route path="industries/:industry" element={<IndustryPage />} />

      {/* Legal - Accessibility */}
      <Route path="accessibility-statement" element={<AccessibilityPage />} />
      
      {/* Case Studies - handled above at line 666 */}
      
      {/* Pricing (Legacy) */}
      <Route path="pricing" element={<PricingPage />} />
      <Route path="quote" element={<QuoteToStartRedirect />} />
      <Route path="guide" element={<BuyerGuidePage />} />
      
      
      {/* Company */}
      <Route path="about" element={<AboutPage />} />
      <Route path="careers-hiring" element={<CareersLandingPage />} />
      <Route path="careers" element={<CareersPage />} />
      <Route path="careers/jobs" element={<CareersPage />} />
      <Route path="careers/jobs/:slug" element={<JobDetailNewPage />} />
      <Route path="careers/freelance" element={<CareersPage />} />
      <Route path="freelancer" element={<LangRedirect to="/careers/freelance" />} />
      <Route path="careers/apply" element={<ApplyPage />} />
      <Route path="careers/dashboard" element={<DashboardPreviewPage />} />
      <Route path="careers/regions" element={<RegionsPage />} />
      <Route path="careers/why-join" element={<WhyJoinPage />} />
      <Route path="careers/auth/login" element={<AuthLoginPage />} />
      <Route path="careers/auth/register" element={<AuthRegisterPage />} />
      <Route path="careers/auth/reset-password" element={<AuthResetPasswordPage />} />
      <Route path="careers/employer/dashboard" element={<EmployerDashboardPage />} />
      <Route path="careers/employer/post-job" element={<EmployerPostJobPage />} />
      <Route path="careers/admin/moderation" element={<AdminModerationPage />} />

      {/* Career Role SEO Landing Pages — 10 roles */}
      {['call-center-agent','team-leader','quality-analyst','web-developer','mobile-developer','ai-engineer','devops-engineer','ux-designer','digital-marketer','content-moderator'].map(slug => (
        <Route key={`career-${slug}`} path={`careers/${slug}`} element={<CareerRolePage />} />
      ))}

      {/* Freelance Category Landing Pages — 10 categories */}
      {['freelance-developers','freelance-designers','freelance-marketers','freelance-data-scientists','freelance-devops','freelance-qa-testers','freelance-project-managers','freelance-content-writers','freelance-seo-experts','freelance-mobile-developers'].map(slug => (
        <Route key={slug} path={slug} element={<FreelanceCategoryPage />} />
      ))}

      {/* Freelancers Directory */}
      <Route path="freelancers" element={<FreelancersDirectoryPage />} />
      <Route path="freelancers/:id" element={<FreelancerCategoryPage />} />

      {/* Mission Detail & Proposal */}
      <Route path="careers/missions/:slug" element={<MissionDetailPage />} />
      <Route path="careers/missions/:slug/propose" element={<ProtectedRoute><SubmitProposalPage /></ProtectedRoute>} />

      {/* Login shortcut */}
      <Route path="login" element={<AuthLoginPage />} />

      {/* Role-based Dashboard Routes (Correctif 37) */}
      <Route path="candidate/dashboard" element={<RoleProtectedRoute minRole={UserRole.CANDIDATE}><CandidateDashboardPage /></RoleProtectedRoute>} />
      <Route path="candidate/applications" element={<RoleProtectedRoute minRole={UserRole.CANDIDATE}><CandidateDashboardPage /></RoleProtectedRoute>} />
      <Route path="candidate/profile" element={<RoleProtectedRoute minRole={UserRole.CANDIDATE}><ProfileSettingsPage /></RoleProtectedRoute>} />
      <Route path="freelancer/dashboard" element={<ProtectedRoute><FreelancerDashboardNewPage /></ProtectedRoute>} />
      <Route path="freelancer/proposals" element={<ProtectedRoute><FreelancerProposalsPage /></ProtectedRoute>} />
      <Route path="freelancer/missions" element={<ProtectedRoute><FreelancerMissionsPage /></ProtectedRoute>} />
      <Route path="freelancer/planning" element={<ProtectedRoute><FreelancerPlanningPage /></ProtectedRoute>} />
      <Route path="freelancer/wallet" element={<ProtectedRoute><FreelancerWalletPage /></ProtectedRoute>} />
      <Route path="freelancer/payments" element={<ProtectedRoute><FreelancerWalletPage /></ProtectedRoute>} />
      <Route path="freelancer/training" element={<ProtectedRoute><FreelancerTrainingPage /></ProtectedRoute>} />
      <Route path="freelancer/reviews" element={<ProtectedRoute><FreelancerReviewsPage /></ProtectedRoute>} />
      <Route path="freelancer/profile" element={<ProtectedRoute><FreelancerProfilePortalPage /></ProtectedRoute>} />
      <Route path="freelancer/settings" element={<ProtectedRoute><FreelancerSettingsPage /></ProtectedRoute>} />
      <Route path="freelancer/workspace/:missionId" element={<ProtectedRoute><FreelancerWorkspacePage /></ProtectedRoute>} />
      <Route path="freelancer/messages" element={<ProtectedRoute><FreelancerMessagesPage /></ProtectedRoute>} />
      <Route path="manager/dashboard" element={<RoleProtectedRoute minRole={UserRole.MANAGER}><ManagerDashboardPage /></RoleProtectedRoute>} />
      <Route path="manager/team" element={<RoleProtectedRoute minRole={UserRole.MANAGER}><ManagerDashboardPage /></RoleProtectedRoute>} />
      <Route path="manager/performance" element={<RoleProtectedRoute minRole={UserRole.MANAGER}><ManagerDashboardPage /></RoleProtectedRoute>} />
      <Route path="hr/dashboard" element={<RoleProtectedRoute minRole={UserRole.HR_MANAGER}><HRDashboardPage /></RoleProtectedRoute>} />
      <Route path="hr/recruitment" element={<RoleProtectedRoute minRole={UserRole.HR_MANAGER}><HRDashboardPage /></RoleProtectedRoute>} />
      <Route path="hr/employees" element={<RoleProtectedRoute minRole={UserRole.HR_MANAGER}><HRDashboardPage /></RoleProtectedRoute>} />
      <Route path="director/dashboard" element={<RoleProtectedRoute minRole={UserRole.DEPARTMENT_HEAD}><DirectorDashboardPage /></RoleProtectedRoute>} />
      <Route path="director/budget" element={<RoleProtectedRoute minRole={UserRole.DEPARTMENT_HEAD}><DirectorDashboardPage /></RoleProtectedRoute>} />
      <Route path="director/objectives" element={<RoleProtectedRoute minRole={UserRole.DEPARTMENT_HEAD}><DirectorDashboardPage /></RoleProtectedRoute>} />
      <Route path="profile/settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
      <Route path="settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
      <Route path="dashboard" element={<UserDashboardPage />} />
      <Route path="profile" element={<UserProfilePage />} />
      <Route path="order" element={<FreelanceOrderWizardPage />} />
      <Route path="unauthorized" element={<UnauthorizedPage />} />

      {/* Client Portal (Correctif 64 — Phase 3) */}
      <Route path="client/dashboard" element={<ProtectedRoute><ClientDashboardNewPage /></ProtectedRoute>} />
      <Route path="client/missions" element={<ProtectedRoute><ClientMissionsPage /></ProtectedRoute>} />
      <Route path="client/contracts" element={<ProtectedRoute><ClientContractsPage /></ProtectedRoute>} />
      <Route path="client/payments" element={<ProtectedRoute><ClientPaymentsNewPage /></ProtectedRoute>} />
      <Route path="client/reviews" element={<ProtectedRoute><ClientReviewsPage /></ProtectedRoute>} />
      <Route path="client/settings" element={<ProtectedRoute><ClientSettingsNewPage /></ProtectedRoute>} />
      <Route path="client/messages" element={<ProtectedRoute><ClientMessagesPage /></ProtectedRoute>} />
      <Route path="client/post-mission" element={<ProtectedRoute><ClientPostMissionPage /></ProtectedRoute>} />
      <Route path="client/missions/:missionId/proposals" element={<ProtectedRoute><ClientProposalReviewPage /></ProtectedRoute>} />
      <Route path="client/contracts/sign/:contractId" element={<ProtectedRoute><ClientContractSigningPage /></ProtectedRoute>} />
      <Route path="client/missions/new" element={<LangRedirect to="/client/post-mission" />} />
      <Route path="client/contract/:id" element={<LangRedirect to="/client/contracts" />} />
      <Route path="client/contract-signing/:id" element={<LangRedirect to="/client/contracts" />} />

      {/* Marketplace (Public) */}
      <Route path="marketplace/freelancers" element={<FreelancerMarketplacePage />} />
      <Route path="marketplace/freelancers/:id" element={<FreelancerPublicProfilePage />} />
      <Route path="marketplace/missions" element={<MissionMarketplacePage />} />
      <Route path="marketplace/missions/:id" element={<MissionDetailPublicPage />} />
      <Route path="freelance/order" element={<LangRedirect to="/order" />} />
      <Route path="freelance/profile/:id" element={<FreelancerProfilePage />} />
      <Route path="admin/super-dashboard" element={<RoleProtectedRoute minRole={UserRole.SUPER_ADMIN}><SuperAdminDashboardPage /></RoleProtectedRoute>} />

      {/* Top-level auth/employer/admin aliases */}
      <Route path="auth/login" element={<AuthLoginPage />} />
      <Route path="auth/register" element={<AuthRegisterPage />} />
      <Route path="auth/reset-password" element={<AuthResetPasswordPage />} />
      <Route path="employer/dashboard" element={<EmployerDashboardPage />} />
      <Route path="employer/post-job" element={<EmployerPostJobPage />} />
      <Route path="admin/moderation" element={<AdminModerationPage />} />
      <Route path="careers/spontaneous" element={<SpontaneousApplicationPage />} />
      <Route path="careers/:slug" element={<JobDetailPage />} />
      <Route path="jobs" element={<JobBoardPage />} />
      <Route path="insights" element={<LangRedirect to="/blog" />} />
      <Route path="insights/:slug" element={<InsightsSlugRedirect />} />
      {/* /blog is canonical and handled above */}
      
      {/* Editorial Guides */}
      <Route path="guides/how-to-choose-a-bpo-partner" element={<HowToChooseBPOPartnerGuide />} />
      <Route path="guides/complete-outsourcing-guide-2026" element={<CompleteOutsourcingGuide2026 />} />
      <Route path="guides/inhouse-vs-outsourcing-cost-analysis" element={<InhouseVsOutsourcingGuide />} />
      <Route path="guides/gdpr-compliance-outsourcing" element={<GDPRComplianceOutsourcingGuide />} />
      <Route path="guides/nearshore-vs-offshore-outsourcing" element={<NearshoreVsOffshoreGuide />} />
      <Route path="guides/ai-call-center-automation-2026" element={<AICallCenterGuide2026 />} />
      <Route path="guides/outsourcing-roi-calculator" element={<OutsourcingROICalculatorGuide />} />
      
      {/* Outsourcing Country Landing Pages — 30 countries */}
      {['outsourcing-morocco','outsourcing-india','outsourcing-philippines','outsourcing-poland','outsourcing-romania','outsourcing-ukraine','outsourcing-egypt','outsourcing-tunisia','outsourcing-senegal','outsourcing-kenya','outsourcing-south-africa','outsourcing-colombia','outsourcing-mexico','outsourcing-brazil','outsourcing-argentina','outsourcing-vietnam','outsourcing-indonesia','outsourcing-malaysia','outsourcing-turkey','outsourcing-pakistan','outsourcing-bangladesh','outsourcing-ghana','outsourcing-nigeria','outsourcing-ivory-coast','outsourcing-cameroon','outsourcing-madagascar','outsourcing-mauritius','outsourcing-czech-republic','outsourcing-bulgaria','outsourcing-serbia'].map(slug => (
        <Route key={slug} path={slug} element={<OutsourcingCountryPage />} />
      ))}

      {/* Outsourcing Country Hub Pages — 10 deep SEO pages */}
      {['morocco','india','philippines','tunisia','egypt','vietnam','poland','romania','mexico','brazil','colombia','senegal','ukraine','argentina','south-africa','malaysia','costa-rica','portugal','kenya','bangladesh'].map(c => (
        <Route key={`hub-${c}`} path={`outsourcing/${c}`} element={<OutsourcingCountryHubPage />} />
      ))}

      <Route path="contact" element={<ContactPage />} />
      <Route path="contact/morocco" element={<ContactOfficePage />} />
      <Route path="contact/spain" element={<ContactOfficePage />} />
      <Route path="contact/:office" element={<ContactOfficePage />} />
      <Route path="leadership" element={<LeadershipPage />} />
      <Route path="about/leadership" element={<LeadershipPage />} />
      <Route path="why-us" element={<WhyUsPage />} />
      
      {/* Redirect prompt-style service slugs to existing pages */}
      <Route path="services/inbound-call-center" element={<LangRedirect to="/services/bpo/inbound-customer-care" />} />
      <Route path="services/outbound-sales" element={<LangRedirect to="/services/bpo/outbound-sales" />} />
      <Route path="services/technical-support" element={<LangRedirect to="/services/bpo/technical-support" />} />
      <Route path="services/chat-email-support" element={<LangRedirect to="/services/bpo/live-chat-messaging" />} />
      <Route path="services/social-media-management" element={<LangRedirect to="/services/bpo/social-media-care" />} />
      <Route path="services/whatsapp-support" element={<LangRedirect to="/services/bpo/whatsapp-business-support" />} />
      <Route path="services/mobile-app-development" element={<LangRedirect to="/services/digital-studio/mobile-apps" />} />
      <Route path="services/saas-development" element={<LangRedirect to="/services/digital-studio/custom-saas" />} />
      <Route path="services/ui-ux-design" element={<LangRedirect to="/services/digital-studio/ui-ux-design" />} />
      <Route path="services/cloud-devops" element={<LangRedirect to="/services/digital-studio/cloud-devops" />} />
      <Route path="services/ai-automation" element={<LangRedirect to="/services/digital-studio/emerging-tech-ai" />} />
      <Route path="services/quality-assurance" element={<LangRedirect to="/services/digital-studio/quality-engineering" />} />
      <Route path="services/bpo/omnichannel" element={<LangRedirect to="/services/bpo/digital-engagement" />} />
      <Route path="services/bpo/market-research" element={<LangRedirect to="/services/bpo/market-research-surveys" />} />
      <Route path="services/bpo/appointment" element={<LangRedirect to="/services/bpo/sdr-appointment-setting" />} />
      <Route path="services/bpo/multilingual" element={<LangRedirect to="/services/bpo/multilingual-support" />} />
      <Route path="get-a-quote" element={<LangRedirect to="/start" />} />
      {/* SEO service slug redirects (from prompt requirements) */}
      <Route path="services/call-center" element={<LangRedirect to="/services/bpo" />} />
      <Route path="services/centre-appel" element={<LangRedirect to="/services/bpo" />} />
      <Route path="services/bpo-outsourcing" element={<LangRedirect to="/services/bpo" />} />
      <Route path="services/sous-traitance" element={<LangRedirect to="/services/bpo" />} />
      <Route path="services/customer-service-outsourcing" element={<LangRedirect to="/services/bpo/inbound-customer-care" />} />
      <Route path="services/technical-support-outsourcing" element={<LangRedirect to="/services/bpo/technical-support" />} />
      <Route path="services/telemarketing" element={<LangRedirect to="/services/bpo/outbound-sales" />} />
      <Route path="services/lead-generation" element={<LangRedirect to="/services/bpo/lead-qualification" />} />
      <Route path="services/back-office-outsourcing" element={<LangRedirect to="/services/bpo/ai-ops-backoffice" />} />
      <Route path="services/multilingual-call-center" element={<LangRedirect to="/services/bpo/multilingual-support" />} />
      {/* Exact-slug redirects for SEO audit recommendations */}
      <Route path="services/outbound-call-center" element={<LangRedirect to="/services/bpo/outbound-sales" />} />
      <Route path="services/live-chat-support" element={<LangRedirect to="/services/bpo/live-chat-messaging" />} />
      <Route path="services/email-support" element={<LangRedirect to="/services/bpo/email-support" />} />
      <Route path="services/back-office-processing" element={<LangRedirect to="/services/bpo/data-entry-processing" />} />
      <Route path="services/multilingual-support" element={<LangRedirect to="/services/bpo/multilingual-support" />} />
      <Route path="services/24-7-support" element={<LangRedirect to="/services/bpo/24-7-support" />} />
      <Route path="industries/ecommerce-support" element={<LangRedirect to="/industries/ecommerce" />} />
      <Route path="industries/travel-hospitality-support" element={<LangRedirect to="/industries/travel-hospitality" />} />
      <Route path="industries/fintech-support" element={<LangRedirect to="/industries/finance" />} />
      <Route path="industries/healthcare-support" element={<LangRedirect to="/industries/healthcare" />} />
      <Route path="industries/saas-support" element={<LangRedirect to="/industries/saas" />} />
      <Route path="industries/insurance-support" element={<LangRedirect to="/industries/insurance" />} />
      <Route path="industries/telecom-support" element={<LangRedirect to="/industries/telecom" />} />
      
      {/* Resources */}
      <Route path="resources" element={<ResourcesPage />} />
      <Route path="proof" element={<ProofPage />} />
      
      {/* Product Pages */}
      <Route path="products" element={<LangRedirect to="/services" />} />
      <Route path="products/neural-agent" element={<NeuralAgentPage />} />
      
      {/* Enterprise Pages */}
      <Route path="trust-center" element={<TrustCenterPage />} />
      <Route path="esg" element={<ESGPage />} />
      <Route path="pricing-models" element={<PricingModelsPage />} />
      <Route path="partnerships" element={<PartnershipsPage />} />
      {/* PILOT-1810h: no validated pilot/free-trial offer — old URLs go to the written-quote request. */}
      <Route path="pilot-in-7-days" element={<PilotToStartRedirect from="pilot-in-7-days" />} />
      <Route path="pilot" element={<PilotToStartRedirect from="pilot" />} />
      <Route path="quickstart" element={<QuickStartPage />} />
      <Route path="newsroom" element={<NewsroomPage />} />
      
      {/* Part 8: New Strategic Pages */}
      <Route path="roi-calculator" element={<ROICalculatorPage />} />
      <Route path="cost-calculator" element={<CostCalculatorPage />} />
      <Route path="pricing-calculator" element={<PricingCalculatorPage />} />
      <Route path="security" element={<SecurityCompliancePage />} />
      <Route path="glossary" element={<GlossaryPage />} />
      <Route path="sla-guarantees" element={<SLAGuaranteesPage />} />
      <Route path="partners" element={<PartnersPage />} />
      <Route path="compare" element={<ComparePage />} />
      <Route path="compare/morocco-vs-india" element={<MoroccoVsIndiaPage />} />
      <Route path="compare/nearshore-vs-offshore" element={<NearshoreVsOffshorePage />} />
      <Route path="compare/services" element={<ServiceComparisonPage />} />
      <Route path="compare/call-center-vs-in-house" element={<ExtendedComparisonPage />} />
      <Route path="compare/morocco-vs-philippines" element={<ExtendedComparisonPage />} />
      <Route path="compare/bpo-providers" element={<ExtendedComparisonPage />} />
      <Route path="compare/bpo-contact-vs-accenture" element={<ComparisonPageV2 />} />
      <Route path="compare/bpo-contact-vs-teleperformance" element={<ComparisonPageV2 />} />
      <Route path="compare/bpo-contact-vs-concentrix" element={<ComparisonPageV2 />} />
      <Route path="compare/bpo-contact-vs-wipro" element={<ComparisonPageV2 />} />
      <Route path="compare/morocco-vs-india-outsourcing" element={<ComparisonPageV2 />} />
      <Route path="compare/morocco-vs-philippines-bpo" element={<ComparisonPageV2 />} />
      <Route path="compare/nearshore-vs-offshore-outsourcing" element={<ComparisonPageV2 />} />
      <Route path="compare/in-house-vs-outsourcing-call-center" element={<ComparisonPageV2 />} />
      <Route path="compare/bpo-vs-kpo" element={<ComparisonPageV2 />} />
      <Route path="compare/onshore-vs-nearshore-vs-offshore" element={<ComparisonPageV2 />} />
      <Route path="compare/outsourcing-morocco-vs-india" element={<ComparisonPageV2 />} />
      <Route path="compare/nearshore-vs-offshore-bpo" element={<ComparisonPageV2 />} />
      {/* Wave 2 VS Comparison Pages */}
      <Route path="compare/morocco-vs-poland" element={<ComparisonLandingPage />} />
      <Route path="compare/morocco-vs-romania" element={<ComparisonLandingPage />} />
      <Route path="compare/morocco-vs-egypt" element={<ComparisonLandingPage />} />
      <Route path="compare/morocco-vs-ukraine" element={<ComparisonLandingPage />} />
      <Route path="compare/morocco-vs-vietnam" element={<ComparisonLandingPage />} />
      <Route path="compare/morocco-vs-colombia" element={<ComparisonLandingPage />} />
      <Route path="compare/morocco-vs-south-africa" element={<ComparisonLandingPage />} />
      <Route path="compare/spain-vs-portugal" element={<ComparisonLandingPage />} />
      <Route path="compare/dubai-vs-india" element={<ComparisonLandingPage />} />
      <Route path="compare/outsourcing-vs-insourcing" element={<ComparisonLandingPage />} />
      {/* New Enterprise Pages */}
      <Route path="fact-sheet" element={<FactSheetPage />} />
      <Route path="esg/governance" element={<ESGGovernancePage />} />
      <Route path="esg/people" element={<ESGPeoplePage />} />
      <Route path="esg/community" element={<ESGCommunityPage />} />
      <Route path="recognitions" element={<RecognitionsPage />} />
      <Route path="faq" element={<FAQHubPage />} />
      <Route path="why-choose-bpo-contact" element={<WhyChooseUsPage />} />
      <Route path="resources/metrics" element={<MetricsLibraryPage />} />
      <Route path="tools/maturity-assessment" element={<MaturityAssessmentPage />} />
      <Route path="sitemap" element={<HTMLSitemapPage />} />
      <Route path="seo-audit" element={<SEOAuditPage />} />
      <Route path="clients" element={<ClientsPage />} />

      {/* Long-Tail Keyword Pages */}
      <Route path="hire-call-center-agents-morocco" element={<LongTailLandingPage />} />
      <Route path="outsource-web-development-morocco" element={<LongTailLandingPage />} />
      <Route path="french-speaking-call-center" element={<LongTailLandingPage />} />
      <Route path="arabic-speaking-call-center" element={<LongTailLandingPage />} />
      <Route path="spanish-speaking-call-center" element={<LongTailLandingPage />} />
      <Route path="multilingual-customer-support" element={<PriorityServicePage />} />
      <Route path="24-7-call-center-services" element={<LongTailLandingPage />} />
      <Route path="offshore-software-development" element={<LongTailLandingPage />} />
      <Route path="nearshore-outsourcing-europe" element={<LongTailLandingPage />} />
      <Route path="bpo-services-for-startups" element={<LongTailLandingPage />} />
      <Route path="enterprise-call-center-solutions" element={<LongTailLandingPage />} />
      <Route path="ai-powered-call-center" element={<LongTailLandingPage />} />
      <Route path="dedicated-development-team" element={<LongTailLandingPage />} />
      <Route path="react-developers-for-hire" element={<LongTailLandingPage />} />
      <Route path="nodejs-developers-for-hire" element={<LongTailLandingPage />} />
      <Route path="python-developers-for-hire" element={<LongTailLandingPage />} />
      <Route path="ecommerce-development-services" element={<LongTailLandingPage />} />
      <Route path="mobile-app-development-outsourcing" element={<LongTailLandingPage />} />
      <Route path="digital-marketing-agency-morocco" element={<LongTailLandingPage />} />
      <Route path="seo-services-outsourcing" element={<LongTailLandingPage />} />

      {/* Long-Tail Extended Pages */}
      <Route path="best-call-center-company-morocco" element={<LongTailLandingPage />} />
      <Route path="affordable-web-development-morocco" element={<LongTailLandingPage />} />
      <Route path="french-speaking-technical-support" element={<LongTailLandingPage />} />
      <Route path="arabic-customer-service-outsourcing" element={<LongTailLandingPage />} />
      <Route path="spanish-call-center-nearshore" element={<LongTailLandingPage />} />
      <Route path="outsource-it-development-africa" element={<LongTailLandingPage />} />
      <Route path="hire-remote-developers-morocco" element={<LongTailLandingPage />} />
      <Route path="call-center-setup-consulting" element={<LongTailLandingPage />} />
      <Route path="bpo-solutions-for-startups" element={<LongTailLandingPage />} />
      <Route path="enterprise-outsourcing-solutions" element={<LongTailLandingPage />} />
      <Route path="24-7-customer-support-outsourcing" element={<LongTailLandingPage />} />
      <Route path="gdpr-compliant-call-center" element={<LongTailLandingPage />} />
      <Route path="" element={<LongTailLandingPage />} />
      <Route path="white-label-call-center" element={<LongTailLandingPage />} />
      <Route path="dedicated-development-team-outsourcing" element={<LongTailLandingPage />} />
      <Route path="mvp-development-services" element={<LongTailLandingPage />} />
      <Route path="ecommerce-development-shopify" element={<LongTailLandingPage />} />
      <Route path="react-developers-for-hire-senior" element={<LongTailLandingPage />} />
      <Route path="cloud-migration-services" element={<LongTailLandingPage />} />
      <Route path="ai-chatbot-development" element={<LongTailLandingPage />} />

      {/* Service + Country Pages */}
      <Route path="call-center/morocco/casablanca" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/morocco/rabat" element={<ServiceCountryLandingPage />} />
      <Route path="web-development/morocco/casablanca" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/spain/barcelona" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/uae/dubai" element={<ServiceCountryLandingPage />} />
      <Route path="web-development/spain/madrid" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/france/paris" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/uk/london" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/germany/berlin" element={<ServiceCountryLandingPage />} />
      <Route path="call-center/usa/new-york" element={<ServiceCountryLandingPage />} />

      {/* BPO Service Landing Pages */}
      <Route path="services/inbound-customer-care" element={<BPOServiceLandingPage />} />
      <Route path="services/outbound-sales" element={<BPOServiceLandingPage />} />
      <Route path="services/technical-support-outsourcing" element={<BPOServiceLandingPage />} />
      <Route path="services/sdr-appointment-setting" element={<BPOServiceLandingPage />} />
      <Route path="services/back-office-data-processing" element={<BPOServiceLandingPage />} />
      <Route path="services/trust-safety-content-moderation" element={<BPOServiceLandingPage />} />
      <Route path="services/live-chat-social-media-support" element={<BPOServiceLandingPage />} />
      <Route path="services/finance-accounting-bpo" element={<BPOServiceLandingPage />} />
      <Route path="services/hr-payroll-bpo" element={<BPOServiceLandingPage />} />
      <Route path="services/healthcare-bpo" element={<BPOServiceLandingPage />} />
      <Route path="services/virtual-assistant" element={<BPOServiceLandingPage />} />
      <Route path="services/customer-retention" element={<BPOServiceLandingPage />} />
      <Route path="services/billing-collections" element={<BPOServiceLandingPage />} />
      <Route path="services/recruitment-staffing-bpo" element={<BPOServiceLandingPage />} />
      <Route path="services/customer-success-saas" element={<BPOServiceLandingPage />} />

      {/* Resource Guide Pages */}
      <Route path="resources/contact-center-outsourcing-scorecard" element={<ContactCenterScorecardPage />} />
      <Route path="large-contact-center-programs" element={<LargeProgramsPage />} />
      <Route path="resources/:slug" element={<ResourceGuidePage />} />

      {/* Service-Specific BPO Landing Pages */}
      <Route path="services/customer-support-outsourcing" element={<BPOServiceLandingPage />} />
      <Route path="services/technical-support-outsourcing-service" element={<BPOServiceLandingPage />} />
      <Route path="services/virtual-assistant-services" element={<BPOServiceLandingPage />} />
      <Route path="services/back-office-outsourcing" element={<BPOServiceLandingPage />} />
      <Route path="services/telemarketing-services" element={<BPOServiceLandingPage />} />
      <Route path="services/appointment-setting-services" element={<BPOServiceLandingPage />} />
      <Route path="services/order-management-outsourcing" element={<BPOServiceLandingPage />} />
      <Route path="services/multilingual-support-services" element={<BPOServiceLandingPage />} />
      <Route path="services/live-chat-support-outsourcing" element={<BPOServiceLandingPage />} />
      <Route path="services/quality-assurance-services" element={<BPOServiceLandingPage />} />
      <Route path="services/data-annotation-services" element={<BPOServiceLandingPage />} />
      <Route path="services/cybersecurity-services-bpo" element={<BPOServiceLandingPage />} />

      <Route path="services/web-development" element={<DigitalStudioLandingPage />} />
      <Route path="services/mobile-app-development" element={<DigitalStudioLandingPage />} />
      <Route path="services/custom-saas-development" element={<DigitalStudioLandingPage />} />
      <Route path="services/enterprise-portals" element={<DigitalStudioLandingPage />} />
      <Route path="services/ui-ux-design" element={<DigitalStudioLandingPage />} />
      <Route path="services/cloud-devops" element={<DigitalStudioLandingPage />} />
      <Route path="services/ai-ml-development" element={<DigitalStudioLandingPage />} />
      <Route path="services/blockchain-web3" element={<DigitalStudioLandingPage />} />
      <Route path="services/iot-development" element={<DigitalStudioLandingPage />} />
      <Route path="services/ar-vr-development" element={<DigitalStudioLandingPage />} />
      <Route path="services/cybersecurity-services" element={<DigitalStudioLandingPage />} />
      <Route path="services/data-engineering" element={<DigitalStudioLandingPage />} />
      <Route path="services/staff-augmentation" element={<DigitalStudioLandingPage />} />
      <Route path="services/legacy-modernization" element={<DigitalStudioLandingPage />} />
      <Route path="services/api-integration" element={<DigitalStudioLandingPage />} />

      {/* Industry Vertical Landing Pages */}
      <Route path="industries/ecommerce" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/fintech" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/healthcare" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/saas" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/telecom" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/real-estate" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/travel-hospitality" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/insurance" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/education" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/logistics" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/automotive" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/energy" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/legal" element={<IndustryVerticalLandingPage />} />
      <Route path="industries/gaming" element={<IndustryVerticalLandingPage />} />

      {/* Transversal Service Landing Pages */}
      <Route path="services/multilingual-call-center" element={<TransversalServiceLandingPage />} />
      <Route path="services/omnichannel-support" element={<PriorityServicePage />} />
      <Route path="services/nearshore-outsourcing" element={<TransversalServiceLandingPage />} />
      <Route path="services/recruitment-staffing" element={<TransversalServiceLandingPage />} />
      <Route path="services/quality-assurance" element={<TransversalServiceLandingPage />} />
      <Route path="services/digital-marketing" element={<TransversalServiceLandingPage />} />
      <Route path="services/consulting-strategy" element={<TransversalServiceLandingPage />} />
      <Route path="services/managed-services" element={<TransversalServiceLandingPage />} />

      {/* Order Now Flow */}
      <Route path="start" element={<OrderWizardPage />} />
      <Route path="start/confirmation" element={<OrderConfirmationPage />} />
      
      {/* Client Portal Control Center - Self-Serve */}
      <Route path="portal" element={<ClientPortalHome />} />
      <Route path="portal/home" element={<ClientPortalHome />} />
      <Route path="portal/catalog" element={<PortalServicesPage />} />
      <Route path="portal/orders" element={<ClientOrdersPage />} />
      <Route path="portal/programs" element={<BPOProgramsPage />} />
      <Route path="portal/projects" element={<ITProjectsPage />} />
      <Route path="portal/tracker" element={<UnifiedTrackerPage />} />
      <Route path="portal/changes" element={<ModificationCenterPage />} />
      <Route path="portal/changes/new" element={<NewChangeRequestPage />} />
      <Route path="portal/services" element={<PortalServicesPage />} />
      <Route path="portal/billing" element={<BillingPage />} />
      <Route path="portal/billing/settings" element={<BillingSettingsPage />} />
      <Route path="portal/settings" element={<ClientSettingsPage />} />
      <Route path="portal/order/bpo" element={<BPOOrderWizardPage />} />
      <Route path="portal/order/it" element={<ITOrderWizardPage />} />
      
      {/* BPO Module Routes */}
      <Route path="portal/bpo" element={<BPODashboard />} />
      <Route path="portal/bpo/deployments" element={<DeploymentTracker />} />
      <Route path="portal/bpo/live-ops" element={<LiveOpsDashboard />} />
      <Route path="portal/bpo/wfm" element={<WFMCenterPage />} />
      <Route path="portal/bpo/quality" element={<QMCenterPage />} />
      <Route path="portal/bpo/kb" element={<KnowledgeBasePage />} />
      <Route path="portal/bpo/changes" element={<ChangeManagementPage />} />
      <Route path="portal/bpo/changes/new" element={<NewChangeRequestPage />} />
      <Route path="portal/bpo/billing" element={<BillingEnginePage />} />
      
      {/* IT Module Routes */}
      <Route path="portal/it" element={<ITDashboard />} />
      <Route path="portal/it/projects" element={<ITDashboard />} />
      <Route path="portal/it/tickets" element={<ITSMTicketsPage />} />
      <Route path="portal/it/tickets/new" element={<ITSMTicketsPage />} />
      <Route path="portal/it/observability" element={<ObservabilityHubPage />} />
      
      {/* Integration & Automation */}
      <Route path="portal/integrations" element={<IntegrationCenterPage />} />
      <Route path="portal/automation" element={<AutomationEnginePage />} />
      <Route path="portal/alerts" element={<AlertCenterPage />} />
      <Route path="portal/security" element={<SecuritySettingsPage />} />
      
      {/* Governance */}
      <Route path="portal/governance/sla" element={<GovernanceSLAPage />} />
      <Route path="portal/governance/sla-designer" element={<SLADesignerPage />} />
      <Route path="portal/governance/playbooks" element={<GovernancePlaybooksPage />} />
      
      {/* Enterprise Features */}
      <Route path="portal/programs/new" element={<ProgramSetupWizard />} />
      <Route path="portal/kb" element={<PortalKnowledgeBasePage />} />
      <Route path="portal/kb/:articleId" element={<PortalKnowledgeBasePage />} />
      <Route path="portal/kb/category/:categoryId" element={<PortalKnowledgeBasePage />} />
      <Route path="portal/requests" element={<RequestsListPage />} />
      <Route path="portal/requests/new" element={<NewRequestPage />} />
      <Route path="portal/requests/:requestId" element={<RequestDetailPage />} />
      <Route path="portal/wallboard" element={<LiveWallboardPage />} />
      <Route path="portal/incidents" element={<CommandCenterPage />} />
      <Route path="portal/exports" element={<DataExportPage />} />
      <Route path="portal/org/users" element={<OrgManagementPage />} />
      <Route path="portal/profile" element={<ProfilePage />} />
      <Route path="portal/releases" element={<ReleaseManagementPage />} />
      
      {/* Telephony Center Routes */}
      <Route path="portal/voice" element={<TelephonyCenterPage />} />
      <Route path="portal/voice/queues" element={<QueuesPage />} />
      <Route path="portal/voice/campaigns" element={<VoiceCampaignsPage />} />
      <Route path="portal/voice/leads" element={<LeadsPage />} />
      <Route path="portal/voice/recordings" element={<RecordingsPage />} />
      <Route path="portal/voice/monitoring" element={<VoiceMonitoringPage />} />
      <Route path="portal/voice/reports" element={<VoiceReportsPage />} />
      <Route path="portal/voice/integrations" element={<VoiceIntegrationsPage />} />
      <Route path="portal/voice/dids" element={<DIDsPage />} />
      <Route path="portal/voice/routes" element={<OutboundRoutesPage />} />
      <Route path="portal/voice/sip" element={<SipTrunksPage />} />
      <Route path="portal/voice/sip/new" element={<SipWizardPage />} />
      <Route path="portal/voice/sip/:id" element={<SipDetailPage />} />
      <Route path="portal/voice/sip/:id/test" element={<SipTestPage />} />
      <Route path="portal/voice/sip/integrations" element={<SipIntegrationsPage />} />
      
      {/* Communications Center Routes */}
      <Route path="portal/mail" element={<CommunicationsCenterPage />} />
      <Route path="portal/mail/campaigns" element={<EmailCampaignsPage />} />
      <Route path="portal/mail/transactional" element={<TransactionalPage />} />
      <Route path="portal/mail/lists" element={<ListsPage />} />
      <Route path="portal/mail/templates" element={<TemplatesPage />} />
      <Route path="portal/mail/deliverability" element={<DeliverabilityPage />} />
      <Route path="portal/mail/suppressions" element={<SuppressionsPage />} />
      <Route path="portal/mail/logs" element={<MailLogsPage />} />
      <Route path="portal/mail/automation" element={<MailAutomationPage />} />
      <Route path="portal/mail/reports" element={<MailReportsPage />} />
      <Route path="portal/mail/preferences" element={<PreferenceCenterPage />} />
      <Route path="portal/mail/servers" element={<PortalEmailServersPage />} />
      
      {/* Admin Routes */}
      <Route path="admin/integrations" element={<IntegrationCenterPage />} />
      <Route path="admin/billing" element={<BillingEnginePage />} />
      <Route path="admin/alerts" element={<AlertCenterPage />} />
      <Route path="admin/security" element={<SecuritySettingsPage />} />
      <Route path="admin/changes" element={<ChangeManagementPage />} />
      <Route path="admin/kb" element={<KnowledgeOpsPage />} />
      <Route path="admin/wallboard" element={<LiveWallboardPage />} />
      <Route path="admin/incidents" element={<CommandCenterPage />} />
      <Route path="admin/audit" element={<DataExportPage />} />
      <Route path="admin/catalog" element={<ServiceCatalogPage />} />
      <Route path="admin/voice" element={<VoiceAdminPage />} />
      <Route path="admin/mail" element={<MailAdminPage />} />
      
      {/* Insights & Analytics */}
      <Route path="portal/insights" element={<InsightsAnalyticsPage />} />
      
      {/* Agent Workspace Routes */}
      <Route path="agent/coaching" element={<AgentCoachingPage />} />
      <Route path="agent/learning" element={<AgentLearningPage />} />

      {/* Utility Pages */}
      <Route path="booking" element={<BookingPage />} />
      {/* BOOKING-CTA-2110: legacy "Book a Call" alias (404 before) -> real slot request, query kept. */}
      <Route path="book-a-call" element={<LangRedirectKeepQuery to="/booking" />} />

      <Route path="whistleblowing" element={<WhistleblowingPage />} />
      <Route path="rfp" element={<RFPPage />} />
      
      {/* Legal */}
      <Route path="legal" element={<LangRedirect to="/legal/terms" />} />
      <Route path="legal/privacy" element={<PrivacyPageNew />} />
      <Route path="legal/terms" element={<TermsPageNew />} />
      <Route path="legal/cookies" element={<CookiesPageNew />} />
      <Route path="legal/accessibility" element={<AccessibilityPage />} />
      <Route path="legal/legal-notice" element={<LegalNoticePage />} />
      {/* Legacy legal route aliases */}
      <Route path="privacy-policy" element={<LangRedirect to="/legal/privacy" />} />
      <Route path="privacy" element={<LangRedirect to="/legal/privacy" />} />
      <Route path="terms-of-service" element={<LangRedirect to="/legal/terms" />} />
      <Route path="terms" element={<LangRedirect to="/legal/terms" />} />
      <Route path="cookie-policy" element={<LangRedirect to="/legal/cookies" />} />
      <Route path="legal-notice" element={<LangRedirect to="/legal/legal-notice" />} />
      
      {/* Trust Ecosystem */}
      <Route path="trust/model" element={<TrustModelPage />} />
      <Route path="trust/data-rights" element={<DataRightsPage />} />
      <Route path="trust/security-assurance" element={<SecurityAssurancePage />} />
      <Route path="trust/ai-governance" element={<AIGovernancePage />} />
      <Route path="trust/security-culture" element={<SecurityCulturePage />} />
      
      {/* Capability Pages */}
      <Route path="capabilities/workforce-management" element={<WorkforceManagementPage />} />
      <Route path="capabilities/quality-assurance" element={<QualityAssurancePage />} />
      <Route path="capabilities/knowledge-management" element={<KnowledgeManagementPage />} />
      <Route path="capabilities/reporting-bi" element={<ReportingBIPage />} />
      <Route path="capabilities/training-onboarding" element={<TrainingOnboardingPage />} />
      <Route path="capabilities/continuous-improvement" element={<ContinuousImprovementPage />} />
      
      {/* Location Pages */}
      <Route path="locations" element={<LocationsPage />} />
      <Route path="cities" element={<CitiesHubPage />} />
      <Route path="locations/morocco" element={<MoroccoPage />} />
      <Route path="locations/spain" element={<SpainPage />} />

      {/* SEO-critical service slug routes — must render real content, not 404 */}
      <Route path="bpo-outsourcing" element={<LangRedirect to="/services/bpo" />} />
      <Route path="it-development" element={<LangRedirect to="/services/digital-studio" />} />
      <Route path="digital-marketing" element={<LangRedirect to="/services/digital-marketing" />} />
      <Route path="regions" element={<LangRedirect to="/services/bpo" />} />
      
      {/* Service × Country programmatic SEO pages */}
      {SERVICE_BY_COUNTRY_PREFIXES.flatMap(prefix =>
        SERVICE_BY_COUNTRY_COUNTRIES.map(country =>
          <Route key={`${prefix}-${country}`} path={`${prefix}-${country}`} element={<ServiceByCountryPage />} />
        )
      )}

      {/* Service × City programmatic SEO pages */}
      {SERVICE_BY_CITY_PREFIXES.flatMap(prefix =>
        SERVICE_BY_CITY_CITIES.map(city =>
          <Route key={`svc-city-${prefix}-${city}`} path={`${prefix}-${city}`} element={<ServiceByCityPage />} />
        )
      )}

      {/* Catch-all: any unmatched slug renders a service overview page (not a 404) */}
      <Route path="*" element={<ServiceSlugCatchAll />} />

      </Routes>
    </LanguageProvider>
  );
};

const helmetContext = {};

const RouteAnalyticsTracker = () => {
  usePageTracking();
  return null;
};

const App = () => (
  <HelmetProvider context={helmetContext}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OrderProvider>
          <TooltipProvider>
            <Suspense fallback={null}><Toaster /></Suspense>
            <Suspense fallback={null}><Sonner /></Suspense>
            <BrowserRouter>
              <RouteAnalyticsTracker />
              <CommercialLinkNormalizer />
              <Suspense fallback={null}><SingleSessionListener /></Suspense>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Root redirect to canonical language */}
                <Route path="/" element={<Navigate to="/en" replace />} />

                {/* WebSite SearchAction endpoint (schema.org SearchAction target) */}
                <Route path="/search" element={<SearchPage />} />
                <Route path="/:lang/search" element={<SearchPage />} />

                {/* Canonical Auth Routes (EN source of truth) */}
                <Route path="/en/auth/login" element={<LoginPage />} />
                <Route path="/en/auth/signup" element={<SignupPage />} />
                <Route path="/en/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/en/auth/reset-password" element={<ResetPasswordPage />} />
                <Route path="/en/auth/verify-email" element={<VerifyEmailPage />} />
                <Route path="/en/auth/invite" element={<InvitePage />} />
                <Route path="/en/auth/accept-invite" element={<InvitePage />} />
                <Route path="/en/auth/mfa" element={<MFAPage />} />
                <Route path="/en/auth/logout" element={<LogoutPage />} />
                <Route path="/en/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/en/auth/error" element={<AuthErrorPage />} />

                {/* OAuth 2.1 consent screen (MCP agent integrations) */}
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                
                
                {/* Legacy Auth Routes → Redirect to canonical */}
                <Route path="/auth" element={<Navigate to="/en/auth/login" replace />} />
                <Route path="/en/auth" element={<AuthPage />} />
                <Route path="/:lang/auth" element={<AuthPage />} />
                <Route path="/en/auth/sign-in" element={<Navigate to="/en/auth/login" replace />} />
                <Route path="/en/auth/sign-up" element={<Navigate to="/en/auth/signup" replace />} />
                <Route path="/en/auth/sign-out" element={<LogoutPage />} />
                <Route path="/login" element={<Navigate to="/en/auth/login" replace />} />
                <Route path="/register" element={<Navigate to="/en/auth/signup" replace />} />
                <Route path="/logout" element={<Navigate to="/en/auth/logout" replace />} />
                <Route path="/portal/login" element={<Navigate to="/en/auth/login" replace />} />
                <Route path="/portal/register" element={<Navigate to="/en/auth/signup" replace />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/account-disabled" element={<AccountDisabledPage />} />
                
                {/* Portal Redirect - intelligent role-based routing */}
                <Route path="/portal" element={<PortalRedirectPage />} />
                
                {/* Platform Setup Wizard - First-run configuration */}
                <Route path="/admin/setup" element={<PlatformSetupWizard />} />
                
                {/* QA Results & Audit Pages (Admin Only) */}
                <Route path="/admin/qa-results" element={
                  <AdminRoute><QAResultsPage /></AdminRoute>
                } />
                <Route path="/admin/link-audit" element={
                  <AdminRoute><LinkAuditPage /></AdminRoute>
                } />
                <Route path="/admin/error-log" element={
                  <AdminRoute><ErrorLogPage /></AdminRoute>
                } />
                <Route path="/admin/gap-report" element={
                  <AdminRoute><GapReportPage /></AdminRoute>
                } />
                <Route path="/admin/global-settings" element={
                  <AdminRoute><GlobalSettingsPage /></AdminRoute>
                } />
                <Route path="/admin/system-health" element={
                  <AdminRoute><SystemHealthPage /></AdminRoute>
                } />
                
                {/* Client Portal */}
                <Route path="/client/dashboard" element={
                  <ClientRoute><ClientDashboard /></ClientRoute>
                } />
                <Route path="/client/tickets" element={
                  <ClientRoute><ClientTickets /></ClientRoute>
                } />
                <Route path="/client/tickets/new" element={
                  <ClientRoute><NewTicket /></ClientRoute>
                } />
                <Route path="/client/tickets/:ticketId" element={
                  <ClientRoute><TicketDetail /></ClientRoute>
                } />
                
                {/* Agent Portal */}
                <Route path="/agent/dashboard" element={
                  <Navigate to="/en/agent" replace />
                } />
                <Route path="/en/agent" element={
                  <AgentRoute><AgentWorkspaceDashboard /></AgentRoute>
                } />
                <Route path="/en/agent/schedule" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="view_schedule">
                      <AgentSchedulePage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/qa" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="view_own_evaluations">
                      <AgentQAPage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/kb" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="view_kb_articles">
                      <AgentKBPage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/time" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="clock_in_out">
                      <AgentTimePage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/announcements" element={
                  <AgentRoute><AgentAnnouncementsPage /></AgentRoute>
                } />
                <Route path="/en/agent/tickets" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="view_case">
                      <AgentTicketsPage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/tickets/:ticketId" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="view_case">
                      <AgentTicketDetailPage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/coaching" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="acknowledge_coaching">
                      <AgentCoachingPage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/learning" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="acknowledge_coaching">
                      <AgentLearningPage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/settings" element={
                  <AgentRoute><AgentSettingsPage /></AgentRoute>
                } />
                <Route path="/en/agent/phone" element={
                  <AgentRoute>
                    <AgentPermissionRoute permission="use_dialpad">
                      <AgentPhonePage />
                    </AgentPermissionRoute>
                  </AgentRoute>
                } />
                <Route path="/en/agent/mail" element={
                  <AgentRoute><AgentMailPage /></AgentRoute>
                } />
                <Route path="/en/agent/coverage" element={
                  <AdminRoute><AgentCoveragePage /></AdminRoute>
                } />
                <Route path="/en/agent/production-readiness" element={
                  <AdminRoute><AgentProductionReadinessPage /></AdminRoute>
                } />
                {/* Admin Portal */}
                <Route path="/admin/onboarding" element={
                  <ProtectedRoute><AdminOnboardingPage /></ProtectedRoute>
                } />
                <Route path="/admin/dashboard" element={
                  <AdminRoute><AdminDashboard /></AdminRoute>
                } />
                <Route path="/admin/reports" element={
                  <AdminRoute><AdminReports /></AdminRoute>
                } />
                <Route path="/admin/users" element={
                  <AdminRoute><AdminUsers /></AdminRoute>
                } />
                <Route path="/admin/tickets" element={
                  <AdminRoute><AdminTickets /></AdminRoute>
                } />
                <Route path="/admin/tickets/:ticketId" element={
                  <AdminRoute><AdminTickets /></AdminRoute>
                } />
                <Route path="/admin/workforce" element={
                  <AdminRoute><WorkforceSchedule /></AdminRoute>
                } />
                <Route path="/admin/schedule" element={
                  <AdminRoute><WorkforceSchedule /></AdminRoute>
                } />
                <Route path="/admin/integrations" element={
                  <AdminRoute><AdminIntegrations /></AdminRoute>
                } />
                <Route path="/admin/clients" element={
                  <AdminRoute><AdminClients /></AdminRoute>
                } />
                <Route path="/admin/clients/:id" element={
                  <AdminRoute><AdminClientDetail /></AdminRoute>
                } />
                <Route path="/admin/subscriptions" element={
                  <AdminRoute><AdminSubscriptions /></AdminRoute>
                } />
                <Route path="/admin/activity" element={
                  <AdminRoute><AdminActivity /></AdminRoute>
                } />
                <Route path="/admin/agents" element={
                  <AdminRoute><AdminAgents /></AdminRoute>
                } />
                <Route path="/admin/settings" element={
                  <AdminRoute><AdminSettingsHub /></AdminRoute>
                } />
                <Route path="/admin/profile" element={
                  <AdminRoute><AdminSettings /></AdminRoute>
                } />
                
                {/* New Admin Routes */}
                <Route path="/admin/tenants" element={<AdminRoute><AdminTenantsPage /></AdminRoute>} />
                <Route path="/admin/rbac" element={<AdminRoute><AdminRBACPage /></AdminRoute>} />
                <Route path="/admin/security" element={<AdminRoute><AdminSecurityPage /></AdminRoute>} />
                <Route path="/admin/programs" element={<AdminRoute><AdminProgramsPage /></AdminRoute>} />
                <Route path="/admin/orders" element={<AdminRoute><AdminOrdersPage /></AdminRoute>} />
                <Route path="/admin/leads" element={<AdminRoute><AdminLeadsPage /></AdminRoute>} />
                <Route path="/admin/recruitment" element={<AdminRoute><AdminRecruitmentPage /></AdminRoute>} />
                <Route path="/admin/freelance" element={<AdminRoute><AdminFreelancePage /></AdminRoute>} />
                <Route path="/admin/escrow" element={<AdminRoute><AdminEscrowPage /></AdminRoute>} />
                <Route path="/admin/disputes" element={<AdminRoute><AdminDisputesPage /></AdminRoute>} />
                <Route path="/admin/platform-invoices" element={<AdminRoute><AdminPlatformInvoicesPage /></AdminRoute>} />
                <Route path="/admin/commission" element={<AdminRoute><AdminCommissionPage /></AdminRoute>} />
                <Route path="/admin/approvals" element={<AdminRoute><AdminApprovalsPage /></AdminRoute>} />
                <Route path="/admin/wfm" element={<AdminRoute><AdminWFMPage /></AdminRoute>} />
                <Route path="/admin/quality" element={<AdminRoute><AdminQualityPage /></AdminRoute>} />
                <Route path="/admin/billing" element={<AdminRoute><AdminBillingFullPage /></AdminRoute>} />
                <Route path="/admin/projects" element={<AdminRoute><AdminProjectsPage /></AdminRoute>} />
                <Route path="/admin/voice" element={<AdminRoute><AdminVoicePage /></AdminRoute>} />
                <Route path="/admin/voice/sip" element={<AdminRoute><AdminSIPPage /></AdminRoute>} />
                <Route path="/admin/voice/dids" element={<AdminRoute><AdminVoiceDIDsPage /></AdminRoute>} />
                <Route path="/admin/voice/queues" element={<AdminRoute><AdminVoiceQueuesPage /></AdminRoute>} />
                <Route path="/admin/voice/campaigns" element={<AdminRoute><AdminVoiceCampaignsPage /></AdminRoute>} />
                <Route path="/admin/voice/monitoring" element={<AdminRoute><AdminVoiceMonitoringPage /></AdminRoute>} />
                <Route path="/admin/voice/logs" element={<AdminRoute><AdminVoiceLogsPage /></AdminRoute>} />
                <Route path="/admin/voice/*" element={<AdminRoute><AdminVoicePage /></AdminRoute>} />
                <Route path="/admin/mail" element={<AdminRoute><AdminMailPage /></AdminRoute>} />
                <Route path="/admin/mail/inbox" element={<AdminRoute><AdminInboxPage /></AdminRoute>} />
                <Route path="/admin/mail/agents" element={<AdminRoute><AdminAgentEmailsPage /></AdminRoute>} />
                <Route path="/admin/api-keys/metadata" element={<AdminRoute><AdminMetadataApiKeyPage /></AdminRoute>} />
                <Route path="/admin/recette-lead" element={<AdminRoute><AdminLeadRecettePage /></AdminRoute>} />
                <Route path="/admin/validated-terms" element={<AdminRoute><AdminValidatedTermsPage /></AdminRoute>} />
                <Route path="/admin/mail/templates" element={<AdminRoute><AdminMailTemplatesPage /></AdminRoute>} />
                <Route path="/admin/mail/campaigns" element={<AdminRoute><AdminMailCampaignsPage /></AdminRoute>} />
                <Route path="/admin/mail/domains" element={<AdminRoute><AdminMailDomainsPage /></AdminRoute>} />
                <Route path="/admin/mail/logs" element={<AdminRoute><AdminMailLogsPage /></AdminRoute>} />
                <Route path="/admin/mail/webhooks" element={<AdminRoute><AdminMailWebhooksPage /></AdminRoute>} />
                <Route path="/admin/mail/deliverability" element={<AdminRoute><AdminDeliverabilityDashboardPage /></AdminRoute>} />
                <Route path="/admin/mail/warmup" element={<AdminRoute><AdminIPWarmupPage /></AdminRoute>} />
                <Route path="/admin/mail/*" element={<AdminRoute><AdminMailPage /></AdminRoute>} />
                <Route path="/admin/wallboard" element={<AdminRoute><LiveWallboardPage /></AdminRoute>} />
                <Route path="/admin/pricing" element={<AdminRoute><AdminPricingPage /></AdminRoute>} />
                <Route path="/admin/audit" element={<AdminRoute><AuditReportPage /></AdminRoute>} />
                <Route path="/admin/staffing" element={<AdminRoute><AdminStaffingPage /></AdminRoute>} />
                <Route path="/admin/security/retention" element={<AdminRoute><AdminDataRetentionPage /></AdminRoute>} />
                <Route path="/admin/audit-logs" element={<AdminRoute><AdminAuditLogsPage /></AdminRoute>} />
                <Route path="/admin/alerts" element={<AdminRoute><AdminAlertsPage /></AdminRoute>} />
                <Route path="/admin/incidents" element={<AdminRoute><AdminIncidentsPage /></AdminRoute>} />
                <Route path="/admin/observability" element={<AdminRoute><AdminObservabilityPage /></AdminRoute>} />
                <Route path="/admin/releases" element={<AdminRoute><AdminReleasesPage /></AdminRoute>} />
                <Route path="/admin/training" element={<AdminRoute><AdminTrainingPage /></AdminRoute>} />
                <Route path="/admin/api-keys" element={<AdminRoute><AdminAPIKeysPage /></AdminRoute>} />
                <Route path="/admin/webhooks" element={<AdminRoute><AdminWebhooksPage /></AdminRoute>} />
                <Route path="/admin/settings/security" element={<AdminRoute><AdminSecuritySettingsPage /></AdminRoute>} />
                
                <Route path="/admin/agent-desktop" element={<AdminRoute><AgentDesktopPage /></AdminRoute>} />
                <Route path="/admin/command-center" element={<AdminRoute><SupervisorCommandCenterPage /></AdminRoute>} />
                <Route path="/admin/simulation" element={<AdminRoute><SimulationModePage /></AdminRoute>} />
                <Route path="/admin/crm/cases" element={<AdminRoute><CRMCasesPage /></AdminRoute>} />
                <Route path="/admin/crm/contacts" element={<AdminRoute><CRMContactsPage /></AdminRoute>} />
                <Route path="/admin/crm/companies" element={<AdminRoute><CRMCompaniesPage /></AdminRoute>} />
                <Route path="/admin/crm/deals" element={<AdminRoute><CRMDealsPage /></AdminRoute>} />
                <Route path="/admin/crm/tickets" element={<AdminRoute><CRMCasesPage /></AdminRoute>} />
                <Route path="/admin/crm/inbox" element={<AdminRoute><CRMInboxPage /></AdminRoute>} />
                <Route path="/admin/campaigns/builder" element={<AdminRoute><CampaignBuilderPage /></AdminRoute>} />
                <Route path="/admin/sales/campaigns" element={<AdminRoute><SalesCampaignsPage /></AdminRoute>} />
                <Route path="/admin/analytics" element={<AdminRoute><AnalyticsDashboardPage /></AdminRoute>} />
                <Route path="/admin/routing" element={<AdminRoute><RoutingEnginePage /></AdminRoute>} />
                <Route path="/admin/knowledge-base" element={<AdminRoute><KnowledgeBaseAdminPage /></AdminRoute>} />
                <Route path="/admin/access-reviews" element={<AdminRoute><AdminAccessReviewsPage /></AdminRoute>} />
                <Route path="/admin/mail/engines" element={<AdminRoute><AdminMailEnginesPage /></AdminRoute>} />
                <Route path="/admin/mail/servers" element={<AdminRoute><EmailServersPage /></AdminRoute>} />
                <Route path="/admin/billing/invoices" element={<AdminRoute><AdminInvoicesPage /></AdminRoute>} />
                <Route path="/admin/billing/usage" element={<AdminRoute><AdminUsagePage /></AdminRoute>} />
                <Route path="/admin/security/policies" element={<AdminRoute><AdminSecurityPoliciesPageNew /></AdminRoute>} />
                <Route path="/admin/security/ssomfa" element={<AdminRoute><AdminSSOPage /></AdminRoute>} />
                <Route path="/admin/catalog" element={<AdminRoute><AdminServiceCatalogPage /></AdminRoute>} />
                <Route path="/admin/automation" element={<AdminRoute><AdminAutomationPage /></AdminRoute>} />
                <Route path="/admin/integrations/logs" element={<AdminRoute><AdminIntegrationLogsPage /></AdminRoute>} />
                <Route path="/admin/integrations/api-keys" element={<AdminRoute><AdminAPIKeysPage /></AdminRoute>} />
                <Route path="/admin/integrations/webhooks" element={<AdminRoute><AdminWebhooksPage /></AdminRoute>} />
                
                {/* WFM, QA, Compliance Routes */}
                <Route path="/admin/wfm/scheduling" element={<AdminRoute><AdminWFMSchedulingPage /></AdminRoute>} />
                <Route path="/admin/qa/scorecards" element={<AdminRoute><AdminQAScorecardsPage /></AdminRoute>} />
                <Route path="/admin/coaching" element={<AdminRoute><AdminCoachingTasksPage /></AdminRoute>} />
                <Route path="/admin/compliance" element={<AdminRoute><AdminDataCompliancePage /></AdminRoute>} />
                <Route path="/admin/tenant-management" element={<AdminRoute><AdminTenantManagementPage /></AdminRoute>} />
                <Route path="/admin/recordings" element={<AdminRoute><AdminRecordingsPage /></AdminRoute>} />
                
                {/* Connector, Health & Testing Routes */}
                <Route path="/admin/connectors" element={<AdminRoute><AdminConnectorsPage /></AdminRoute>} />
                <Route path="/admin/health-endpoints" element={<AdminRoute><AdminHealthEndpointsPage /></AdminRoute>} />
                <Route path="/admin/e2e-tests" element={<AdminRoute><AdminE2ETestsPage /></AdminRoute>} />
                <Route path="/admin/seo-audit" element={<AdminRoute><AdminSEOAuditPage /></AdminRoute>} />
                <Route path="/admin/gsc-monitor" element={<AdminRoute><AdminGSCMonitorPage /></AdminRoute>} />
                <Route path="/admin/og-inspector" element={<AdminRoute><AdminOGInspectorPage /></AdminRoute>} />
                <Route path="/admin/status" element={<AdminRoute><AdminStatusPage /></AdminRoute>} />
                <Route path="/admin/checklist" element={<AdminRoute><AZChecklistPage /></AdminRoute>} />
                <Route path="/admin/seo-analytics" element={<AdminRoute><AdminSEOAnalyticsPage /></AdminRoute>} />
                
                {/* Phase 1: Project Map, Health & Simulation */}
                <Route path="/admin/sitemap" element={<AdminRoute><AdminSitemapPage /></AdminRoute>} />
                <Route path="/admin/health" element={<AdminRoute><AdminHealthPage /></AdminRoute>} />
                <Route path="/admin/tests" element={<AdminRoute><AdminE2ETestsPage /></AdminRoute>} />
                <Route path="/admin/routing/simulation" element={<AdminRoute><AdminRoutingSimulationPage /></AdminRoute>} />
                
                {/* Phase 2: Routing Module */}
                <Route path="/admin/routing/skills" element={<AdminRoute><AdminRoutingSkillsPage /></AdminRoute>} />
                <Route path="/admin/routing/slas" element={<AdminRoute><AdminRoutingSLAsPage /></AdminRoute>} />
                <Route path="/admin/routing/policies" element={<AdminRoute><AdminRoutingPoliciesPage /></AdminRoute>} />
                <Route path="/admin/routing/dispositions" element={<AdminRoute><AdminRoutingDispositionsPage /></AdminRoute>} />
                <Route path="/admin/routing/schedules" element={<AdminRoute><AdminRoutingSchedulesPage /></AdminRoute>} />
                
                {/* Phase 2: WFM Module */}
                <Route path="/admin/wfm/forecast" element={<AdminRoute><AdminWFMForecastPage /></AdminRoute>} />
                <Route path="/admin/wfm/adherence" element={<AdminRoute><AdminWFMAdherencePage /></AdminRoute>} />
                <Route path="/admin/wfm/intraday" element={<AdminRoute><AdminWFMIntradayPage /></AdminRoute>} />
                <Route path="/admin/wfm/time-settings" element={<AdminRoute><AdminTimeSettingsPage /></AdminRoute>} />
                
                {/* Phase 2: Analytics Module */}
                <Route path="/admin/analytics/realtime" element={<AdminRoute><AdminAnalyticsRealtimePage /></AdminRoute>} />
                <Route path="/admin/analytics/exports" element={<AdminRoute><AdminAnalyticsExportsPage /></AdminRoute>} />
                
                {/* Phase 2: Quality Module */}
                <Route path="/admin/quality/forms" element={<AdminRoute><AdminQualityFormsPage /></AdminRoute>} />
                <Route path="/admin/quality/evaluations" element={<AdminRoute><AdminQualityEvaluationsPage /></AdminRoute>} />
                <Route path="/admin/quality/coaching" element={<AdminRoute><AdminQualityCoachingPage /></AdminRoute>} />
                
                {/* Phase 2: Digital Module */}
                <Route path="/admin/digital/channels" element={<AdminRoute><AdminDigitalChannelsPage /></AdminRoute>} />
                <Route path="/admin/digital/templates" element={<AdminRoute><AdminDigitalTemplatesPage /></AdminRoute>} />
                <Route path="/admin/digital/routing" element={<AdminRoute><AdminDigitalRoutingPage /></AdminRoute>} />
                
                {/* Phase 2: Security Module */}
                <Route path="/admin/security/sso" element={<AdminRoute><AdminSecuritySSOPage /></AdminRoute>} />
                <Route path="/admin/security/mfa" element={<AdminRoute><AdminSecurityMFAPage /></AdminRoute>} />
                {/* security/retention handled above */}
                <Route path="/admin/security/pii-masking" element={<AdminRoute><AdminSecurityPIIMaskingPage /></AdminRoute>} />
                
{/* Phase 2: E2E Tests Page */}
                {/* admin/tests handled above */}
                
                {/* Phase 2: Integrations Module */}
                <Route path="/admin/integrations/webhooks-v2" element={<AdminRoute><AdminIntegrationsWebhooksPage /></AdminRoute>} />
                <Route path="/admin/integrations/api-keys-v2" element={<AdminRoute><AdminIntegrationsAPIKeysPage /></AdminRoute>} />
                <Route path="/admin/integrations/cti" element={<AdminRoute><AdminIntegrationsCTIPage /></AdminRoute>} />
                <Route path="/admin/integrations/crm" element={<AdminRoute><AdminIntegrationsCRMPage /></AdminRoute>} />
                
                {/* Phase 2: Voice Module */}
                <Route path="/admin/voice/numbers" element={<AdminRoute><AdminVoiceNumbersPage /></AdminRoute>} />
                <Route path="/admin/voice/trunks" element={<AdminRoute><AdminVoiceTrunksPage /></AdminRoute>} />
                <Route path="/admin/voice/ivr" element={<AdminRoute><AdminVoiceIVRPage /></AdminRoute>} />
                
                {/* Phase 2: Settings Module */}
                <Route path="/admin/settings/feature-flags" element={<AdminRoute><AdminSettingsFeatureFlagsPage /></AdminRoute>} />
                <Route path="/admin/settings/environments" element={<AdminRoute><AdminSettingsEnvironmentsPage /></AdminRoute>} />
                
                {/* Live Ops & Agent Emails */}
                <Route path="/admin/live-ops" element={<AdminRoute><AdminLiveOpsPage /></AdminRoute>} />
                <Route path="/admin/agent-emails" element={<AdminRoute><AdminAgentEmailsManagementPage /></AdminRoute>} />
                
                {/* Redirect /admin/invoices to /admin/platform-invoices */}
                <Route path="/admin/invoices" element={<Navigate to="/admin/platform-invoices" replace />} />
                
                {/* Redirect /admin to /admin/dashboard */}
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                
                {/* IT-ALIAS-0411: legacy root URL -> public IT request form (no 404, no price/order) */}
                <Route path={IT_ORDER_ALIAS_PATH} element={<Navigate to={IT_ORDER_ALIAS_TARGET} replace />} />

                {/* QUOTE-ALIAS-0411: legacy root /quote -> public BPO quote form (keeps query string) */}
                <Route path="/quote" element={<QuoteToStartRedirect />} />

                {/* CONTACT-ALIAS-0511: bare /contact -> English commercial contact page (keeps search + hash) */}
                <Route path="/contact" element={<LangRedirectKeepQuery to="/contact" />} />

                {/* Localized routes - LanguageProvider wraps all */}
                <Route path="/:lang/*" element={<LocalizedRoutes />} />
                
                {/* Catch-all 404 */}
                <Route path="*" element={<Suspense fallback={null}><NotFoundPage /></Suspense>} />
              </Routes>
              
              {/* Global UI Components */}
              <DynamicTrackingScripts />
              <CookieConsentManager />
              <BackToTop />
              <GlobalBookingModal />
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </OrderProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
